import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronRight, Copy, DollarSign, Percent, Plus, RefreshCw, Trash2 } from "lucide-react";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import * as clientsApi from "@/pages/clients/clients.api";
import * as plansApi from "@/pages/clients/plans.api";
import * as productsApi from "@/pages/products/products.api";
import {
  EProductType,
  isProductTypeAllowingZeroPlanPremium,
} from "@/pages/products/products.type";
import { isInfinitySyncedProduct } from "@/pages/products/products-sync-origin";
import * as projectsApi from "@/pages/projects/projects.api";
import { formatPrice } from "@/utils/monetary-formatter";

import { usePlanForm } from "./plan-form.hooks";
import { TPlanFormData } from "./plan-form.types";

export const PlanFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams<{ clientId: string }>();
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Obter projectId dos params também, caso esteja na URL
  const params = useParams<{ projectId?: string }>();

  const state = location.state as {
    selectedProducts?: any[];
    selectedCapitalRanges?: any[]; // Capital ranges selecionados
    projectSelectedCapitalRanges?: any[]; // Capital ranges associados ao projeto
    editingPlanoIndex?: number;
    initialData?: TPlanFormData;
    returnPath?: string;
    isEditing?: boolean;
    planId?: string; // ID do plano quando vem da página de projetos
    projectId?: string; // ID do projeto quando vem da página de projetos
    projectType?: "affinity" | "affiliate";
    projectPartners?: Array<{
      id: string;
      name: string;
      commission_percentage: number;
      level_hierarchy: number;
    }>;
    projectFather?: {
      id: string;
      name: string;
    };
    projectParticipantOrganizations?: Array<{
      id: string;
      name: string;
    }>;
    projectLevelHierarchical?: number; // Quantidade de níveis definida no projeto (apenas para affiliate)
    projectHunters?: Array<{
      id: string;
      name: string;
    }>;
  } | null;

  const selectedProductsFromState = state?.selectedProducts || [];
  const selectedCapitalRangesFromState = state?.selectedCapitalRanges || [];
  const projectSelectedCapitalRangesFromState =
    state?.projectSelectedCapitalRanges || [];
  const editingPlanoIndex = state?.editingPlanoIndex;
  const initialData = state?.initialData;
  const returnPath = state?.returnPath || (clientId ? `/clients/${clientId}/plans` : "/clients");
  const planId = state?.planId; // ID do plano quando estiver editando
  const isEditingPlan = state?.isEditing || !!planId; // Flag para indicar se está editando
  const projectId = state?.projectId || params.projectId; // Tentar pegar da URL também
  const projectType = state?.projectType;
  const projectPartners = state?.projectPartners;
  const projectFather = state?.projectFather;
  const projectParticipantOrganizations = state?.projectParticipantOrganizations || [];
  const projectLevelHierarchical = state?.projectLevelHierarchical;
  const projectHunters = state?.projectHunters || [];
  
  // Ref para evitar redirecionamentos múltiplos
  const hasRedirectedRef = React.useRef(false);
  
  // Verificar se precisa redirecionar quando o state está ausente (refresh da página)
  React.useEffect(() => {
    // Se já redirecionou, não fazer novamente
    if (hasRedirectedRef.current) {
      return;
    }
    
    // Se não há state e não há dados essenciais, redirecionar
    // Para edição: precisa de planId no state (não pode ser recuperado da URL)
    // Para criação: se não há state e não há projectId na URL, redirecionar
    const hasNoState = !state;
    const isEditingWithoutPlanId = isEditingPlan && !planId;
    // Se está criando, só redirecionar se não há state E não há projectId na URL
    const isCreatingWithoutData = !isEditingPlan && hasNoState && !projectId;
    
    if (isEditingWithoutPlanId || isCreatingWithoutData) {
      hasRedirectedRef.current = true;
      
      // Se tem projectId (da URL ou state), voltar para detalhes do projeto
      if (projectId && clientId) {
        setTimeout(() => {
          navigate(`/clients/${clientId}/projects/${projectId}`, { replace: true });
        }, 0);
        return;
      }
      
      // Caso contrário, voltar para a lista de planos ou clientes
      setTimeout(() => {
        if (clientId) {
          navigate(`/clients/${clientId}/plans`, { replace: true });
        } else {
          navigate("/clients", { replace: true });
        }
      }, 0);
    }
  }, [state, isEditingPlan, planId, projectId, clientId, navigate]);
  
  // Buscar dados do cliente para usar na busca do plano
  const { data: client } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => {
      if (!clientId) return null;
      return clientsApi.getClientById(clientId);
    },
    enabled: !!clientId && !!isEditingPlan && !!planId && !hasRedirectedRef.current,
    staleTime: 60_000,
    retry: false,
  });
  
  // Buscar dados do projeto para usar na busca do plano
  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => {
      if (!projectId) return null;
      return projectsApi.getProjectById(projectId);
    },
    enabled: !!projectId && !!isEditingPlan && !!planId && !hasRedirectedRef.current,
    staleTime: 60_000,
    retry: false,
  });
  
  // Buscar dados do plano quando estiver editando
  const { data: planToEdit } = useQuery({
    queryKey: ["plan", planId, projectId],
    queryFn: async () => {
      if (!planId || planId === "undefined") {
        return null;
      }
      
      if (
        projectId &&
        (project?.type_client === "affinity" || project?.type_client === "affiliate") &&
        client?.id
      ) {
        // Para planos dentro de projeto (affinity/affiliate), usar a rota que retorna o formato novo
        return projectsApi.getPlanWithCommissions(planId, client.id);
      } else {
        // Usar a rota antiga para planos normais
        if (!clientId) return null;
        return plansApi.getPlanById(clientId, planId);
      }
    },
    enabled:
      isEditingPlan &&
      !!planId &&
      planId !== "undefined" &&
      !!client?.id &&
      !!projectId &&
      !!project &&
      !hasRedirectedRef.current,
    staleTime: 60_000,
    retry: false,
  });

  // Buscar produtos do projeto se projectId estiver presente
  const { data: projectProducts = [] } = useQuery({
    queryKey: ["project-products", projectId],
    queryFn: () => {
      if (!projectId) throw new Error("ProjectId is required");
      return projectsApi.getProjectProducts(projectId);
    },
    enabled: !!projectId && !hasRedirectedRef.current,
    staleTime: 60_000,
    retry: false,
  });

  // Buscar detalhes completos dos produtos do projeto
  const { data: allProducts = [] } = useQuery({
    queryKey: ["all-products"],
    queryFn: () => productsApi.getProducts(),
    enabled: !!projectId && !hasRedirectedRef.current,
    staleTime: 60_000,
    retry: false,
  });

  // Filtrar produtos completos baseado nos produtos do projeto, mostrando apenas as opções selecionadas
  const projectProductsFull = React.useMemo(() => {
    // Se houver capital ranges associados ao projeto (estado vindo da edição),
    // usar apenas esses ranges para montar a lista
    if (projectSelectedCapitalRangesFromState.length > 0 && allProducts.length > 0) {
      const expandedProducts: any[] = [];

      projectSelectedCapitalRangesFromState.forEach((cr: any) => {
        const baseProduct = allProducts.find((p) => p.id === cr.product_id);
        if (!baseProduct) {
          return;
        }

        const fullRange = baseProduct.capital_ranges?.find((r: any) => r.id === cr.id);
        const rangeData = fullRange || cr;

        expandedProducts.push({
          id: rangeData.id || cr.id,
          product_id: cr.product_id,
          name: rangeData.plan_name || rangeData.description || baseProduct.name || "",
          type: baseProduct.type || "",
          capital_value: rangeData.capital_value,
          premium_value: rangeData.premium_value,
          description: rangeData.description || "",
          minimum_price: rangeData.minimum_price ?? null,
          plan_name: rangeData.plan_name ?? null,
          insurance_policy_extra_price_id:
            (baseProduct as any).insurance_policy_extra_price_id ?? null,
          capital_ranges: [],
        });
      });

      if (expandedProducts.length > 0) {
        return expandedProducts;
      }
    }

    // Se houver produtos selecionados no state (vindos da navegação), usar esses diretamente
    // Na edição, priorizar os dados completos do projeto para garantir plan_name correto
    if (!isEditingPlan && selectedProductsFromState.length > 0 && selectedCapitalRangesFromState.length > 0) {
      // Expandir cada capital range selecionado como um produto separado
      const expandedProducts: any[] = [];
      
      selectedCapitalRangesFromState.forEach((cr) => {
        // Encontrar o produto base correspondente
        const baseProduct = selectedProductsFromState.find(
          (p) => p.product_id === cr.product_id || p.id === cr.product_id
        ) || selectedProductsFromState.find((p) => p.id === cr.id);
        
        if (baseProduct) {
          // Criar um produto separado para cada capital range
          expandedProducts.push({
            id: cr.id, // Usar o ID do capital range
            product_id: cr.product_id || baseProduct.product_id || baseProduct.id,
            name: cr.plan_name || cr.description || baseProduct.name || "",
            type: baseProduct.type || cr.type || "",
            capital_value: cr.capital_value,
            premium_value: cr.premium_value,
            description: cr.description || "",
            minimum_price: cr.minimum_price ?? null,
            plan_name: cr.plan_name ?? null,
            insurance_policy_extra_price_id: baseProduct.insurance_policy_extra_price_id ?? null,
            capital_ranges: [], // Não precisa de capital_ranges pois já é um capital range expandido
          });
        }
      });
      
      // Se não encontrou capital ranges, usar os produtos do state diretamente
      if (expandedProducts.length > 0) {
        return expandedProducts;
      }
      
      return selectedProductsFromState;
    }
    
    if (!projectId || projectProducts.length === 0) {
      return selectedProductsFromState;
    }
    
    // Buscar detalhes completos dos produtos do projeto
    const fullProducts: any[] = [];
    
    projectProducts.forEach((projectProduct) => {
      const fullProduct = allProducts.find((p) => p.id === projectProduct.id);
      if (fullProduct) {
        // Verificar se o produto tem capital ranges
        const hasCapitalRanges = fullProduct.capital_ranges && fullProduct.capital_ranges.length > 0;
        
        if (hasCapitalRanges) {
          // SEMPRE expandir TODOS os capital ranges do produto na edição
          // Isso garante que todos os capital ranges estejam disponíveis para seleção
          const allRanges = fullProduct.capital_ranges || [];
          
          // Expandir cada capital range como um produto separado
          allRanges.forEach((range: any, index: number) => {
            // Garantir que sempre temos um ID válido
            const rangeId = range.id || `${fullProduct.id}-${index}-${range.description || range.capital_value || Date.now()}`;
            fullProducts.push({
              id: rangeId, // Usar o ID do capital range
              product_id: fullProduct.id, // ID do produto base
              name: (range as any).plan_name || range.description || fullProduct.name || "",
              type: fullProduct.type || "",
              capital_value: range.capital_value,
              premium_value: range.premium_value,
              description: range.description || "",
              minimum_price: (range as any).minimum_price ?? null,
              plan_name: (range as any).plan_name ?? null,
              insurance_policy_extra_price_id: (fullProduct as any).insurance_policy_extra_price_id ?? null,
              capital_ranges: [], // Não precisa de capital_ranges pois já é um capital range expandido
            });
          });
        } else {
          // Produto sem capital ranges - mostrar o produto principal
          fullProducts.push({
            id: fullProduct.id,
            product_id: fullProduct.id,
            name: fullProduct.name || "",
            type: fullProduct.type || "",
            capital_value: fullProduct.capital_value || 0,
            premium_value: fullProduct.premium_value || 0,
            description: fullProduct.description || "",
            minimum_price: (fullProduct as any).minimum_price ?? null,
            plan_name: (fullProduct as any).plan_name ?? null,
            insurance_policy_extra_price_id: (fullProduct as any).insurance_policy_extra_price_id ?? null,
            capital_ranges: [],
          });
        }
      }
    });
    
    return fullProducts.length > 0 ? fullProducts : selectedProductsFromState;
  }, [
    projectId,
    projectProducts,
    allProducts,
    selectedProductsFromState,
    selectedCapitalRangesFromState,
    projectSelectedCapitalRangesFromState,
    isEditingPlan,
    planToEdit,
  ]);

  // Mapa para lookup rápido dos campos sync por product_id
  const allProductsSyncMap = React.useMemo(
    () => new Map(allProducts.map((p) => [p.id, p])),
    [allProducts]
  );

  const {
    tempPlano,
    setTempPlano,
    validationErrors,
    setValidationErrors,
    validateForm,
    isEditing,
  } = usePlanForm(initialData, editingPlanoIndex, projectFather, projectParticipantOrganizations);


  // Combinar produtos do state com produtos do plano quando estiver editando
  // Isso garante que os produtos do plano apareçam na lista mesmo que não estejam em selectedProducts
  // Se projectId estiver presente, usar apenas os produtos do projeto
  const [effectiveSelectedProducts, setEffectiveSelectedProducts] = React.useState(
    projectId ? projectProductsFull : selectedProductsFromState
  );

  const [expandedInstallments, setExpandedInstallments] = React.useState<Set<number>>(new Set([0]));

  const toggleInstallment = (index: number) => {
    setExpandedInstallments((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const [bulkPartnerValues, setBulkPartnerValues] = React.useState<
    Record<number, { agency: string; commission: string }>
  >({});
  
  React.useEffect(() => {
    // Não executar se já redirecionamos
    if (hasRedirectedRef.current) {
      return;
    }
    
    // Se projectId estiver presente, SEMPRE usar projectProductsFull (todos os capital ranges)
    // Isso garante que na edição todos os capital ranges estejam disponíveis para seleção
    if (projectId) {
      // Sempre usar projectProductsFull para mostrar todos os capital ranges do projeto
      // Isso é especialmente importante na edição para mostrar TODOS os capital ranges disponíveis
      if (projectProductsFull && projectProductsFull.length > 0) {
        setEffectiveSelectedProducts(projectProductsFull);
      } else if (selectedProductsFromState.length > 0) {
        // Fallback: se projectProductsFull não estiver disponível ainda, usar selectedProductsFromState
        setEffectiveSelectedProducts(selectedProductsFromState);
      }
      return;
    }
    
    if (isEditing && tempPlano.products && tempPlano.products.length > 0) {
      // Se houver produtos no state, priorizar esses
      if (selectedProductsFromState.length > 0) {
        setEffectiveSelectedProducts(selectedProductsFromState);
        return;
      }
      
      // Expandir cada produto do plano como um produto separado
      // Isso garante que cada capital range seja tratado individualmente
      const productsFromPlan = tempPlano.products.map((p: any) => ({
        id: p.id || p.product_id,
        product_id: p.product_id || p.id,
        name: p.plan_name || p.description || p.name || "",
        type: p.type || "",
        capital_value: p.capital_value || 0,
        premium_value: p.premium_value || 0,
        description: p.description || "",
        minimum_price: p.minimum_price ?? null,
        plan_name: p.plan_name ?? null,
        insurance_policy_extra_price_id: p.insurance_policy_extra_price_id ?? null,
        capital_ranges: [], // Não precisa de capital_ranges pois já é um capital range expandido
      }));
      
      // Combinar produtos únicos usando uma chave mais específica para evitar duplicatas
      // Para assistências, usar product_id + description + plan_name + minimum_price
      // Para seguros, usar product_id + capital_value + premium_value
      const allProductsCombined = [...selectedProductsFromState, ...productsFromPlan];
      const uniqueProducts = new Map<string, any>();
      
      allProductsCombined.forEach((p: any) => {
        // Criar uma chave única baseada no tipo de produto
        let key: string;
        
        if (p.type === EProductType.INSURANCE_ASSISTANCE) {
          // Para assistências, usar product_id + description + plan_name + minimum_price
          key = `${p.product_id || p.id}-${p.description || ''}-${p.plan_name || ''}-${p.minimum_price || 0}`;
        } else {
          // Para seguros, usar product_id + capital_value + premium_value + description + plan_name
          key = `${p.product_id || p.id}-${p.capital_value || 0}-${p.premium_value || 0}-${p.description || ''}-${p.plan_name || ''}`;
        }
        
        // Se a chave não existe ou se o ID é mais específico, usar este produto
        if (!uniqueProducts.has(key) || (p.id && !uniqueProducts.get(key)?.id)) {
          uniqueProducts.set(key, p);
        }
      });
      
      setEffectiveSelectedProducts(Array.from(uniqueProducts.values()));
    } else {
      setEffectiveSelectedProducts(selectedProductsFromState);
    }
  }, [isEditing, tempPlano.products, selectedProductsFromState, projectId, projectProductsFull, allProducts, projectProducts]);
  
  const selectedProducts = effectiveSelectedProducts;

  // Sincronizar project_type com o projectType do projeto
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (projectType && tempPlano.project_type !== projectType) {
      setTempPlano((prev) => ({
        ...prev,
        project_type: projectType,
      }));
    }
  }, [projectType, tempPlano.project_type, setTempPlano]);

  // Sincronizar produtos quando estiver editando e initialData/produtos estiver disponível
  // Usar ref para evitar loops infinitos
  const hasInitializedProducts = React.useRef(false);
  
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (isEditing && initialData && !hasInitializedProducts.current) {
      // Atualizar todos os dados do plano, não apenas os produtos
      setTempPlano((prev) => {
        const updated = {
          ...prev,
          name: initialData.name || prev.name,
          description: initialData.description || prev.description,
          plan_features: initialData.plan_features || prev.plan_features,
          markup: initialData.markup ?? prev.markup,
          markup_mode: initialData.markup_mode || prev.markup_mode,
          products: initialData.products || prev.products,
          installments: initialData.installments || prev.installments || [],
          otherInstallments: initialData.otherInstallments || prev.otherInstallments,
          commission_distribution: initialData.commission_distribution || prev.commission_distribution,
          commission_lifetime_other_installments: initialData.commission_lifetime_other_installments ?? prev.commission_lifetime_other_installments,
        };
        return updated;
      });
      
      hasInitializedProducts.current = true;
    }
  }, [isEditing, initialData, setTempPlano]);
  
  // Resetar o flag quando sair do modo de edição
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (!isEditing) {
      hasInitializedProducts.current = false;
    }
  }, [isEditing]);

  // Ref para controlar se já inicializou os parceiros
  const hasInitializedPartners = React.useRef(false);

  // Atualizar parceiros quando novas parcelas são adicionadas
  // Só inicializa se não houver dados do initialData (para evitar sobrescrever dados carregados)
  // Funciona para projetos de afinidade e afiliados
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (!projectFather || hasInitializedPartners.current || (isEditing && initialData)) {
      return;
    }
    
    // Para projetos afiliados, também precisamos inicializar os parceiros
    // mesmo que não tenhamos projectParticipantOrganizations (eles serão adicionados depois)

    setTempPlano((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      // Preencher Parceiro Principal nas parcelas que ainda não têm
      // Preservar valores existentes de agency e commission
      if (updated.installments && updated.installments.length > 0) {
        const updatedInstallments = updated.installments.map((installment) => {
          if (!installment.mainPartner?.id || !installment.mainPartner?.name) {
            hasChanges = true;
            return {
              ...installment,
              mainPartner: {
                id: projectFather.id,
                name: projectFather.name,
                agency: installment.mainPartner?.agency ?? 0,
                commission: installment.mainPartner?.commission ?? 0,
              },
            };
          } else if (installment.mainPartner.id === projectFather.id && (!installment.mainPartner.name || installment.mainPartner.name === "")) {
            // Se o ID está correto mas o nome está vazio, atualizar apenas o nome
            hasChanges = true;
            return {
              ...installment,
              mainPartner: {
                ...installment.mainPartner,
                name: projectFather.name,
              },
            };
          }
          return installment;
        });

        if (hasChanges) {
          updated.installments = updatedInstallments;
        }
      }

      // Preencher Parceiros participantes nas parcelas que ainda não têm
      // Verificar se já existem parceiros para evitar duplicação
      if (projectParticipantOrganizations && projectParticipantOrganizations.length > 0 && updated.installments && updated.installments.length > 0) {
        const updatedInstallments = updated.installments.map((installment) => {
          const existingPartnerIds = (installment.participatingPartners || []).map((p: any) => p.id);
          const missingPartners = projectParticipantOrganizations.filter(
            (org) => !existingPartnerIds.includes(org.id)
          );
          
          if (missingPartners.length > 0) {
            hasChanges = true;
            return {
              ...installment,
              participatingPartners: [
                ...(installment.participatingPartners || []),
                ...missingPartners.map((org) => ({
                  id: org.id,
                  name: org.name,
                  agency: 0,
                  commission: 0,
                })),
              ],
            };
          }
          return installment;
        });

        if (hasChanges) {
          updated.installments = updatedInstallments;
        }
      }

      // Preencher otherInstallments.mainPartner se não tiver
      if (!updated.otherInstallments?.mainPartner?.id || !updated.otherInstallments?.mainPartner?.name) {
        hasChanges = true;
        updated.otherInstallments = {
          ...updated.otherInstallments,
          mainPartner: {
            id: projectFather.id,
            name: projectFather.name,
            commission: updated.otherInstallments?.mainPartner?.commission ?? 0,
            markup: updated.otherInstallments?.mainPartner?.markup ?? 0,
          },
          participatingPartners: updated.otherInstallments?.participatingPartners || [],
        };
      }

      // Preencher otherInstallments.participatingPartners se não tiver
      // Para projetos afiliados, projectParticipantOrganizations pode estar vazio (serão adicionados depois)
      if (projectParticipantOrganizations && projectParticipantOrganizations.length > 0) {
        const existingPartnerIds = (updated.otherInstallments?.participatingPartners || []).map((p: any) => p.id);
        const missingPartners = projectParticipantOrganizations.filter(
          (org) => !existingPartnerIds.includes(org.id)
        );
        
        if (missingPartners.length > 0) {
          hasChanges = true;
          updated.otherInstallments = {
            ...updated.otherInstallments,
            mainPartner: updated.otherInstallments?.mainPartner || {
              id: projectFather.id,
              name: projectFather.name,
              commission: 0,
              markup: 0,
            },
            participatingPartners: [
              ...(updated.otherInstallments?.participatingPartners || []),
              ...missingPartners.map((org) => ({
                id: org.id,
                name: org.name,
                commission: 0,
                markup: 0,
              })),
            ],
          };
        }
      }

      // Só retorna o objeto atualizado se houver mudanças para evitar loops
      if (hasChanges) {
        hasInitializedPartners.current = true;
        return updated;
      }
      return prev;
    });
  }, [projectFather, projectParticipantOrganizations, setTempPlano, isEditing, initialData]);

  // Ref para controlar se já inicializou os hunters
  const hasInitializedHunters = React.useRef(false);

  // Atualizar hunters quando novas parcelas são adicionadas
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (!projectHunters || projectHunters.length === 0 || hasInitializedHunters.current || (isEditing && initialData)) {
      return;
    }
    
    setTempPlano((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      // Preencher Hunters nas parcelas que ainda não têm
      if (updated.installments && updated.installments.length > 0) {
        const updatedInstallments = updated.installments.map((installment) => {
          const existingHunterIds = (installment.hunters || []).map((h: any) => h.id);
          const missingHunters = projectHunters.filter(
            (hunter) => !existingHunterIds.includes(hunter.id)
          );
          
          if (missingHunters.length > 0) {
            hasChanges = true;
            return {
              ...installment,
              hunters: [
                ...(installment.hunters || []),
                ...missingHunters.map((hunter) => ({
                  id: hunter.id,
                  name: hunter.name,
                  agency: 0,
                  commission: 0,
                })),
              ],
            };
          }
          return installment;
        });

        if (hasChanges) {
          updated.installments = updatedInstallments;
        }
      }

      // Preencher otherInstallments.hunters se não tiver
      const existingHunterIds = (updated.otherInstallments?.hunters || []).map((h: any) => h.id);
      const missingHunters = projectHunters.filter(
        (hunter) => !existingHunterIds.includes(hunter.id)
      );
      
      if (missingHunters.length > 0) {
        hasChanges = true;
        updated.otherInstallments = {
          ...updated.otherInstallments,
          mainPartner: updated.otherInstallments?.mainPartner || {
            id: projectFather?.id || "",
            name: projectFather?.name || "",
            commission: 0,
            markup: 0,
          },
          participatingPartners: updated.otherInstallments?.participatingPartners || [],
          hunters: [
            ...(updated.otherInstallments?.hunters || []),
            ...missingHunters.map((hunter) => ({
              id: hunter.id,
              name: hunter.name,
              commission: 0,
              markup: 0,
            })),
          ],
        };
      }

      // Só retorna o objeto atualizado se houver mudanças para evitar loops
      if (hasChanges) {
        hasInitializedHunters.current = true;
        return updated;
      }
      return prev;
    });
  }, [projectHunters, projectFather, setTempPlano, isEditing, initialData]);

  // Função para limpar erro quando características são modificadas
  const clearPlanFeaturesError = React.useCallback(() => {
    if (validationErrors.planFeatures) {
      setValidationErrors((prev) => ({ ...prev, planFeatures: undefined }));
    }
  }, [validationErrors.planFeatures, setValidationErrors]);

  // Ref para controlar se já carregou os dados do plano
  const hasLoadedPlanData = React.useRef<string | null>(null);

  // Processar dados do plano quando carregado (para edição)
  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    if (!isEditingPlan || !planId || !planToEdit || hasLoadedPlanData.current === planId) {
      return;
    }

    // Marcar como carregado imediatamente para evitar processamento duplicado
    hasLoadedPlanData.current = planId;

    // Verificar se é formato novo (projeto de afinidade) ou formato antigo
    const isNewFormat =
      (project?.type_client === "affinity" || project?.type_client === "affiliate") &&
      planToEdit.commission_distribution &&
      typeof planToEdit.commission_distribution === "object" &&
      "main_partner" in planToEdit.commission_distribution;

    if (isNewFormat) {
      const commissionDist = planToEdit.commission_distribution as any;
      const commissionAnother = (planToEdit as any).commission_another || {
        main_partner: [],
        partner_participant: [],
        partner_affiliate: [],
        hunter: [],
      };

      const projectParticipants = project?.organization_participants || [];
      const participantOrgMap = new Map<string, string>();
      projectParticipants.forEach((org: any) => {
        participantOrgMap.set(org.id, org.name);
      });

      const mainPartnerId = client?.id || "";
      const mainPartnerName = client?.name || "";

      const normalizePosition = (raw: any): number => {
        const n = Number(raw ?? 0);
        return n > 0 ? n : 1;
      };

      const installmentsMap = new Map<number, any>();

      const ensureInstallment = (position: number) => {
        if (!installmentsMap.has(position)) {
          installmentsMap.set(position, {
            id: `installment-${position}`,
            position,
            mainPartner: {
              id: mainPartnerId,
              name: mainPartnerName,
              agency: 0,
              commission: 0,
            },
            participatingPartners: [],
            hunters: [],
          });
        }
        return installmentsMap.get(position);
      };

      const ensureParticipatingPartner = (installment: any, orgId: string) => {
        const existingIndex = (installment.participatingPartners || []).findIndex(
          (p: any) => p.id === orgId
        );
        if (existingIndex !== -1) {
          return installment.participatingPartners[existingIndex];
        }
        const newPartner = {
          id: orgId,
          name: participantOrgMap.get(orgId) || "",
          agency: 0,
          commission: 0,
        };
        installment.participatingPartners = [
          ...(installment.participatingPartners || []),
          newPartner,
        ];
        return newPartner;
      };

      const ensureHunter = (installment: any, hunterId: string) => {
        const existingIndex = (installment.hunters || []).findIndex(
          (h: any) => h.id === hunterId
        );
        if (existingIndex !== -1) {
          return installment.hunters[existingIndex];
        }
        const hunter = projectHunters.find((h: any) => h.id === hunterId);
        if (!hunter) return null;
        const newHunter = {
          id: hunter.id,
          name: hunter.name,
          agency: 0,
          commission: 0,
        };
        installment.hunters = [
          ...(installment.hunters || []),
          newHunter,
        ];
        return newHunter;
      };

      // Processar commission_distribution (parcelas normais)
      const allCommissionTiers = [
        ...(commissionDist.main_partner || []),
        ...(commissionDist.partner_participant || []),
      ];

      allCommissionTiers.forEach((tier: any) => {
        if (!tier?.organization_id) return;
        const pos = normalizePosition(tier.position);
        const installment = ensureInstallment(pos);
        const agency = Number(tier.agency || 0) / 100;
        const commission = Number(tier.commission || 0) / 100;

        if (tier.organization_id === mainPartnerId) {
          installment.mainPartner = {
            id: mainPartnerId,
            name: mainPartnerName,
            agency,
            commission,
          };
        } else {
          const partner = ensureParticipatingPartner(installment, tier.organization_id);
          partner.agency = agency;
          partner.commission = commission;
        }
      });

      // Processar hunters das parcelas normais
      if (commissionDist.hunter && Array.isArray(commissionDist.hunter)) {
        commissionDist.hunter.forEach((hunterTier: any) => {
          if (!hunterTier?.organization_id) return;
          const pos = normalizePosition(hunterTier.position);
          const installment = ensureInstallment(pos);
          const agency = Number(hunterTier.agency || 0) / 100;
          const commission = Number(hunterTier.commission || 0) / 100;
          
          const hunter = ensureHunter(installment, hunterTier.organization_id);
          if (hunter) {
            hunter.agency = agency;
            hunter.commission = commission;
          }
        });
      }

      // Garantir que todos os participantes e hunters do projeto estejam presentes
      // IMPORTANTE: Se um parceiro participante também é hunter, ele DEVE aparecer em ambos os arrays
      // com os valores corretos de cada fonte (commission_distribution.partner_participant vs commission_distribution.hunter)
      installmentsMap.forEach((installment: any) => {
        // Adicionar todos os parceiros participantes (mesmo que também sejam hunters)
        projectParticipants.forEach((org: any) => {
          ensureParticipatingPartner(installment, org.id);
        });
        // Adicionar todos os hunters (mesmo que também sejam parceiros participantes)
        projectHunters.forEach((hunter: any) => {
          ensureHunter(installment, hunter.id);
        });
      });

      const installments = Array.from(installmentsMap.values()).sort(
        (a, b) => a.position - b.position
      );

      // Processar commission_another (demais parcelas)
      const allAnotherTiers = [
        ...(commissionAnother.main_partner || []),
        ...(commissionAnother.partner_participant || []),
      ];

      const mainPartnerAnother = allAnotherTiers.find(
        (t: any) => t?.organization_id === mainPartnerId
      );

      // Processar hunters das demais parcelas
      // IMPORTANTE: Se um parceiro participante também é hunter, usar os valores corretos de cada fonte
      const huntersMap = new Map<string, any>();
      
      // Primeiro, processar hunters que têm dados específicos em commission_another.hunter
      (commissionAnother.hunter || []).forEach((hunterTier: any) => {
        if (!hunterTier?.organization_id) return;
        const hunter = projectHunters.find((h: any) => h.id === hunterTier.organization_id);
        if (!hunter) return;
        huntersMap.set(hunter.id, {
          id: hunter.id,
          name: hunter.name,
          markup: Number(hunterTier.markup || 0),
          commission: Number(hunterTier.commission || 0) / 100,
        });
      });

      // Processar parceiros participantes das demais parcelas
      const participatingPartners = projectParticipants.map((org: any) => {
        const tier = allAnotherTiers.find(
          (t: any) => t?.organization_id === org.id
        );
        return {
          id: org.id,
          name: org.name,
          markup: tier ? Number(tier.markup || 0) : 0,
          commission: tier ? Number(tier.commission || 0) / 100 : 0,
        };
      });

      // Para hunters que também são parceiros participantes mas não têm dados específicos de hunter,
      // garantir que apareçam na lista de hunters com valores zerados (não usar valores do parceiro)
      // IMPORTANTE: Se um hunter também é parceiro participante, ele deve aparecer em ambos os arrays
      // mas com valores independentes (hunter tem seus próprios valores, parceiro tem seus próprios valores)
      const finalHunters: any[] = [];
      projectHunters.forEach((hunter: any) => {
        const hunterData = huntersMap.get(hunter.id);
        if (hunterData) {
          // Se tem dados específicos de hunter, usar esses
          finalHunters.push(hunterData);
        } else {
          // Se não tem dados específicos, usar valores zerados (não usar valores do parceiro)
          finalHunters.push({
            id: hunter.id,
            name: hunter.name,
            markup: 0,
            commission: 0,
          });
        }
      });

      const otherInstallments = {
        mainPartner: {
          id: mainPartnerId,
          name: mainPartnerName,
          markup: mainPartnerAnother ? Number(mainPartnerAnother.markup || 0) : 0,
          commission: mainPartnerAnother
            ? Number(mainPartnerAnother.commission || 0) / 100
            : 0,
        },
        participatingPartners: participatingPartners,
        hunters: finalHunters,
      };

      // Processar affiliateCommissions se for projeto afiliado
      let affiliateCommissions: any = undefined;
      let affiliateOtherCommissions: any = undefined;
      if (project?.type_client === "affiliate") {
        const positionToIndex = new Map<number, number>();
        installments.forEach((inst: any, idx: number) => {
          positionToIndex.set(inst.position, idx);
        });

        affiliateCommissions = {};
        (commissionDist.partner_affiliate || []).forEach((pa: any) => {
          const idx = positionToIndex.get(normalizePosition(pa.position));
          if (idx === undefined) return;
          if (!affiliateCommissions[idx]) affiliateCommissions[idx] = {};
          affiliateCommissions[idx][pa.level] = {
            agency: (pa.agency || 0) / 100,
            commission: (pa.commission || 0) / 100,
          };
        });

        affiliateOtherCommissions = {};
        (commissionAnother.partner_affiliate || []).forEach((pa: any) => {
          affiliateOtherCommissions[pa.level] = {
            markup: (pa.markup || 0) / 100,
            commission: (pa.commission || 0) / 100,
          };
        });
      }

      // Atualizar tempPlano com os dados carregados
      // Usar uma função de atualização para evitar dependências desnecessárias
      setTempPlano((prev) => {
        // Verificar se já foi atualizado para evitar loops
        if (prev.name === planToEdit.name && prev.installments?.length === installments.length && prev.products.length === (planToEdit.products || []).length) {
          return prev;
        }
        
        // Usar a mesma lógica da criação: mapear produtos do plano para capital ranges em projectProductsFull
        const mappedProducts = (planToEdit.products || []).map((product: any) => {
          // Encontrar o capital range correspondente em projectProductsFull usando a mesma lógica da criação
          let matchingCapitalRange = null;
          
          if (projectProductsFull && projectProductsFull.length > 0) {
            if (product.type === EProductType.INSURANCE_ASSISTANCE) {
              // Para assistências: tentar match completo; se faltar plan_name/minimum_price, usar description
              const productId = product.product_id || product.id;
              const description = product.description || "";
              const planName = product.plan_name || "";
              const minimumPrice = Number(product.minimum_price || 0);

              matchingCapitalRange = projectProductsFull.find((p: any) => {
                const productIdMatch = (p.product_id || p.id) === productId;
                if (!productIdMatch) return false;

                const descriptionMatch = (p.description || "") === description;
                const planNameMatch = (p.plan_name || "") === planName;
                const minimumPriceMatch = Number(p.minimum_price || 0) === minimumPrice;

                return descriptionMatch && planNameMatch && minimumPriceMatch;
              });

              if (!matchingCapitalRange && description) {
                matchingCapitalRange = projectProductsFull.find((p: any) => {
                  const productIdMatch = (p.product_id || p.id) === productId;
                  if (!productIdMatch) return false;
                  return (p.description || "") === description;
                });
              }

              if (!matchingCapitalRange && planName) {
                matchingCapitalRange = projectProductsFull.find((p: any) => {
                  const productIdMatch = (p.product_id || p.id) === productId;
                  if (!productIdMatch) return false;
                  return (p.plan_name || "") === planName;
                });
              }
            } else {
              // Para seguros: product_id + capital_value + premium_value
              matchingCapitalRange = projectProductsFull.find((p: any) => {
                const productIdMatch = (p.product_id || p.id) === (product.product_id || product.id);
                if (!productIdMatch) return false;
                
                const capitalMatch = Number(p.capital_value || 0) === Number(product.capital_value || 0);
                const premiumMatch = Number(p.premium_value || 0) === Number(product.premium_value || 0);
                
                return capitalMatch && premiumMatch;
              });
            }
          }
          
          // Se encontrou, usar os dados do capital range encontrado, mas preservar valores do plano editado
          if (matchingCapitalRange) {
            return {
              id: matchingCapitalRange.id, // ID do capital range (deve corresponder ao id em projectProductsFull)
              product_id: matchingCapitalRange.product_id,
              // IMPORTANTE: Usar plan_name do produto do plano editado (não do matchingCapitalRange)
              // Isso garante que o nome correto seja exibido
              name: product.plan_name || matchingCapitalRange.plan_name || matchingCapitalRange.description || product.description || matchingCapitalRange.name || "",
              type: matchingCapitalRange.type || "",
              capital_value: matchingCapitalRange.capital_value,
              premium_value: product.premium_value || matchingCapitalRange.premium_value || 0, // Manter valor editado
              description: matchingCapitalRange.description || product.description || "",
              minimum_price: matchingCapitalRange.minimum_price ?? product.minimum_price ?? null,
              // IMPORTANTE: Preservar plan_name do produto do plano editado
              plan_name: product.plan_name ?? matchingCapitalRange.plan_name ?? null,
              // IMPORTANTE: Preservar insurance_policy_extra_price_id do produto do plano editado
              insurance_policy_extra_price_id: product.insurance_policy_extra_price_id ?? matchingCapitalRange.insurance_policy_extra_price_id ?? null,
            };
          }
          
          // Fallback: usar dados do produto do plano
          return {
            id: product.id || product.product_id,
            product_id: product.product_id || product.id,
            name: product.plan_name || product.description || product.name || "",
            type: product.type || "",
            capital_value: product.capital_value || 0,
            premium_value: product.premium_value || 0,
            description: product.description || "",
            minimum_price: product.minimum_price ?? null,
            plan_name: product.plan_name ?? null,
            insurance_policy_extra_price_id: product.insurance_policy_extra_price_id ?? null,
          };
        });
        
        return {
          ...prev,
          name: planToEdit.name || prev.name,
          description: planToEdit.description || prev.description,
          plan_features: (planToEdit as any).plan_features || prev.plan_features || [],
          products: mappedProducts,
          installments: installments,
          otherInstallments: otherInstallments,
          affiliateCommissions,
          affiliateOtherCommissions,
          plan_price: planToEdit.plan_price || 0,
        };
      });
      
      // IMPORTANTE: Na edição, sempre usar projectProductsFull (todos os capital ranges do projeto)
      // Não usar apenas os produtos do plano editado, pois precisamos mostrar TODOS os capital ranges
      // para que o usuário possa selecionar/deselecionar
      // Atualizar effectiveSelectedProducts quando o plano for carregado
      if (projectId && projectProductsFull && projectProductsFull.length > 0) {
        setEffectiveSelectedProducts(projectProductsFull);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingPlan, planId, planToEdit?.name, planToEdit?.description, planToEdit?.products?.length, project?.type_client, client?.id, projectHunters.length, projectParticipantOrganizations.length, projectProductsFull, projectId]);

  // Mutation para criar plano diretamente no projeto
  const createPlanMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!projectId) {
        throw new Error("ID do projeto não encontrado");
      }
      return projectsApi.createPlansInProject(projectId, [data]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast({
        title: t("generic.success"),
        description: "Plano criado e associado ao projeto com sucesso!",
      });
      // Redirecionar para a página de detalhes do projeto
      if (projectId && clientId) {
        navigate(`/clients/${clientId}/projects/${projectId}`);
      } else {
        navigate(returnPath.replace('/create-plan', ''));
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao criar plano",
        variant: "destructive",
      });
    },
  });

  // Mutation para atualizar plano diretamente no projeto
  const updatePlanMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!projectId || !planId) {
        throw new Error("ID do projeto ou plano não encontrado");
      }
      return projectsApi.updatePlanInProject("", projectId, planId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["plan", planId] });
      toast({
        title: t("generic.success"),
        description: "Plano atualizado com sucesso!",
      });
      // Redirecionar para a página de detalhes do projeto
      const effectiveProjectId = projectId || params.projectId;
      const effectiveClientId = clientId;
      
      if (effectiveProjectId && effectiveClientId) {
        // Sempre redirecionar para a página de detalhes do projeto quando for edição de plano de projeto
        navigate(`/clients/${effectiveClientId}/projects/${effectiveProjectId}`);
      } else if (returnPath) {
        // Se não tiver projectId, usar returnPath removendo '/create-plan' se existir
        const projectDetailPath = returnPath.replace('/create-plan', '');
        navigate(projectDetailPath);
      } else if (effectiveClientId) {
        // Fallback: voltar para a lista de planos do cliente
        navigate(`/clients/${effectiveClientId}/plans`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao atualizar plano",
        variant: "destructive",
      });
    },
  });

  const handleSaveClick = React.useCallback(() => {
    if (!validateForm()) {
      return; // Não salva se houver erros de validação
    }

    // Preparar dados para salvar
    const dataToSave = {
      ...tempPlano,
      installments: tempPlano.installments || [],
      otherInstallments: tempPlano.otherInstallments || undefined,
      // Incluir comissões por nível para projetos afiliados
      affiliateCommissions: projectType === "affiliate" ? (tempPlano as any).affiliateCommissions : undefined,
      affiliateOtherCommissions: projectType === "affiliate" ? (tempPlano as any).affiliateOtherCommissions : undefined,
    };

    // Se estiver editando um plano de projeto, atualizar diretamente via API
    if (isEditingPlan && planId && projectId && (projectType === "affinity" || projectType === "affiliate")) {
      // A lógica de transformação será reutilizada da seção de criação abaixo
      // Por enquanto, vamos continuar para a seção de criação que já tem toda a lógica
    }

    // Se for um projeto (projectId existe), criar o plano diretamente via API
    if (projectId && (projectType === "affinity" || projectType === "affiliate")) {
      
      // Transformar dados do tempPlano para o formato esperado pela API
      const installments = dataToSave.installments || [];
      const otherInstallments = dataToSave.otherInstallments;
      
      // Processar installments para commission_distribution
      const mainPartnerCommissions: Array<{
        organization_id: string;
        position: number;
        agency: number;
        commission: number;
      }> = [];
      const partnerParticipantCommissions: Array<{
        organization_id: string;
        position: number;
        agency: number;
        commission: number;
      }> = [];
      const hunterCommissions: Array<{
        organization_id: string;
        position: number;
        agency: number;
        commission: number;
      }> = [];
      
      installments.forEach((installment: any, index: number) => {
        if (installment.mainPartner && installment.mainPartner.id) {
          const agencyValue = installment.mainPartner.agency !== undefined ? installment.mainPartner.agency : 0;
          const commissionValue = installment.mainPartner.commission !== undefined ? installment.mainPartner.commission : 0;
          mainPartnerCommissions.push({
            organization_id: installment.mainPartner.id,
            position: installment.position !== undefined ? installment.position : index,
            agency: Math.round(agencyValue * 100),
            commission: Math.round(commissionValue * 100),
          });
        }
        
        if (installment.participatingPartners && Array.isArray(installment.participatingPartners)) {
          installment.participatingPartners.forEach((partner: any) => {
            if (partner.id) {
              const agencyValue = partner.agency !== undefined ? partner.agency : 0;
              const commissionValue = partner.commission !== undefined ? partner.commission : 0;
              partnerParticipantCommissions.push({
                organization_id: partner.id,
                position: installment.position !== undefined ? installment.position : index,
                agency: Math.round(agencyValue * 100),
                commission: Math.round(commissionValue * 100),
              });
            }
          });
        }
        
        // Processar hunters das parcelas
        if (installment.hunters && Array.isArray(installment.hunters)) {
          installment.hunters.forEach((hunter: any) => {
            if (hunter.id) {
              const agencyValue = hunter.agency !== undefined ? hunter.agency : 0;
              const commissionValue = hunter.commission !== undefined ? hunter.commission : 0;
              hunterCommissions.push({
                organization_id: hunter.id,
                position: installment.position !== undefined ? installment.position : index,
                agency: Math.round(agencyValue * 100),
                commission: Math.round(commissionValue * 100),
              });
            }
          });
        }
      });
      
      // Processar otherInstallments (demais parcelas)
      const mainPartnerAnother: Array<{
        organization_id: string;
        markup: number;
        commission: number;
      }> = [];
      const partnerParticipantAnother: Array<{
        organization_id: string;
        markup: number;
        commission: number;
      }> = [];
      const hunterAnother: Array<{
        organization_id: string;
        markup: number;
        commission: number;
      }> = [];
      
      if (otherInstallments) {
        if (otherInstallments.mainPartner && otherInstallments.mainPartner.id) {
          const markupValue = otherInstallments.mainPartner.markup !== undefined ? otherInstallments.mainPartner.markup : 0;
          const commissionValue = otherInstallments.mainPartner.commission !== undefined ? otherInstallments.mainPartner.commission : 0;
          mainPartnerAnother.push({
            organization_id: otherInstallments.mainPartner.id,
            markup: Math.round(markupValue),
            commission: Math.round(commissionValue * 100),
          });
        }
        
        if (otherInstallments.participatingPartners && Array.isArray(otherInstallments.participatingPartners)) {
          otherInstallments.participatingPartners.forEach((partner: any) => {
            if (partner.id) {
              const markupValue = partner.markup !== undefined ? partner.markup : 0;
              const commissionValue = partner.commission !== undefined ? partner.commission : 0;
              partnerParticipantAnother.push({
                organization_id: partner.id,
                markup: Math.round(markupValue),
                commission: Math.round(commissionValue * 100),
              });
            }
          });
        }
        
        // Processar hunters das demais parcelas
        if (otherInstallments.hunters && Array.isArray(otherInstallments.hunters)) {
          otherInstallments.hunters.forEach((hunter: any) => {
            if (hunter.id) {
              const markupValue = hunter.markup !== undefined ? hunter.markup : 0;
              const commissionValue = hunter.commission !== undefined ? hunter.commission : 0;
              hunterAnother.push({
                organization_id: hunter.id,
                markup: Math.round(markupValue),
                commission: Math.round(commissionValue * 100),
              });
            }
          });
        }
      }
      
      // Processar partner_affiliate se for projeto afiliado
      const partnerAffiliateCommissions: Array<{
        level: number;
        position: number;
        agency: number;
        commission: number;
      }> = [];
      const partnerAffiliateAnother: Array<{
        level: number;
        markup: number;
        commission: number;
      }> = [];
      
      if (projectType === "affiliate") {
        const affiliateCommissions = dataToSave.affiliateCommissions || {};
        Object.keys(affiliateCommissions).forEach((installmentIndexStr) => {
          const installmentIndex = parseInt(installmentIndexStr, 10);
          const installment = installments[installmentIndex];
          if (installment) {
            const levelCommissions = affiliateCommissions[installmentIndex] || {};
            Object.keys(levelCommissions).forEach((levelStr) => {
              const level = parseInt(levelStr, 10);
              const commission = levelCommissions[level];
              if (commission !== undefined && commission !== null) {
                partnerAffiliateCommissions.push({
                  level,
                  position: installment.position !== undefined ? installment.position : installmentIndex,
                  agency: Math.round((commission.agency || 0) * 100),
                  commission: Math.round((commission.commission || 0) * 100),
                });
              }
            });
          }
        });
        
        const affiliateOtherCommissions = dataToSave.affiliateOtherCommissions || {};
        Object.keys(affiliateOtherCommissions).forEach((levelStr) => {
          const level = parseInt(levelStr, 10);
          const commission = affiliateOtherCommissions[level];
          if (commission !== undefined && commission !== null) {
            partnerAffiliateAnother.push({
              level,
              markup: Math.round((commission.markup || 0) * 100),
              commission: Math.round((commission.commission || 0) * 100),
            });
          }
        });
      }
      
      // Validar que temos pelo menos um main_partner
      if (mainPartnerCommissions.length === 0 && projectFather?.id) {
        mainPartnerCommissions.push({
          organization_id: projectFather.id,
          position: 0,
          agency: 0,
          commission: 0,
        });
      }
      
      if (mainPartnerCommissions.length === 0) {
        toast({
          title: t("generic.error"),
          description: "Plano não possui parceiro principal configurado.",
          variant: "destructive",
        });
        return;
      }
      
      // Validar produtos antes de criar o plano
      let products;
      try {
        products = (dataToSave.products || []).map((product: any) => {
          // Para assistências, o premium_value pode ser 0 inicialmente e será preenchido pelo usuário
          // Para seguro auto, o premium_value pode ser 0 (salvo com prêmio zerado)
          // Para outros produtos, validar que premium_value é um número válido e positivo
          const isAssistance = product.type === EProductType.INSURANCE_ASSISTANCE;
          const allowsZeroPremium = isProductTypeAllowingZeroPlanPremium(
            product.type,
          );
          const rawPremium = product.premium_value;
          const premiumEmpty =
            rawPremium === undefined ||
            rawPremium === null ||
            rawPremium === "";
          const premiumValue = premiumEmpty
            ? allowsZeroPremium
              ? 0
              : 0
            : Number(rawPremium);

          // Validar premium: positivo exceto assistência (venda), auto/moto/crédito (jornada/LP pode ser 0)
          if (
            !isAssistance &&
            !allowsZeroPremium &&
            (isNaN(premiumValue) || premiumValue <= 0)
          ) {
            throw new Error(
              `Produto "${product.name || "Sem nome"}" deve ter um valor premium maior que zero. Valor atual: ${product.premium_value}`,
            );
          }

          if (isAssistance && (isNaN(premiumValue) || premiumValue <= 0)) {
            throw new Error(
              `Produto "${product.name || "Sem nome"}" deve ter um valor de venda maior que zero. Valor atual: ${product.premium_value}`,
            );
          }

          if (
            allowsZeroPremium &&
            !premiumEmpty &&
            !Number.isFinite(premiumValue)
          ) {
            throw new Error(
              `Produto "${product.name || "Sem nome"}" deve ter um valor premium numérico válido. Valor atual: ${product.premium_value}`,
            );
          }
          
          // IMPORTANTE: product_id sempre deve ser o UUID do produto principal, não o ID do capital range
          // O product_id é o UUID do produto base (ex: "ASSISTÊNCIA FUNERAL")
          // A API identifica o capital range específico através de:
          // - Para assistências: description + plan_name + minimum_price
          // - Para seguros: capital_value + premium_value
          // 
          // Se product.product_id existe, usar ele (é o UUID do produto principal)
          // Se não existir, buscar em projectProductsFull ou allProducts para encontrar o produto base
          let productId = product.product_id;
          
          // Se não tiver product_id, tentar encontrar o produto base
          if (!productId && projectProductsFull && projectProductsFull.length > 0) {
            // Encontrar o capital range correspondente em projectProductsFull
            const matchingCapitalRange = projectProductsFull.find((p: any) => {
              if (p.id === product.id) {
                return true;
              }
              
              // Comparar por características do capital range
              if (product.type === EProductType.INSURANCE_ASSISTANCE) {
                return (
                  (p.product_id || p.id) === (product.product_id || product.id) &&
                  (p.description || '') === (product.description || '') &&
                  (p.plan_name || '') === (product.plan_name || '') &&
                  Number(p.minimum_price || 0) === Number(product.minimum_price || 0)
                );
              } else {
                return (
                  (p.product_id || p.id) === (product.product_id || product.id) &&
                  Number(p.capital_value || 0) === Number(product.capital_value || 0) &&
                  Number(p.premium_value || 0) === Number(product.premium_value || 0)
                );
              }
            });
            
            if (matchingCapitalRange && matchingCapitalRange.product_id) {
              productId = matchingCapitalRange.product_id;
            }
          }
          
          // Se ainda não encontrou, tentar em allProducts
          if (!productId && allProducts.length > 0) {
            const baseProduct = allProducts.find((p) => {
              // Tentar encontrar pelo product_id primeiro
              if (product.product_id && p.id === product.product_id) {
                return true;
              }
              
              // Se não encontrar, tentar pelos capital ranges
              if (p.capital_ranges && p.capital_ranges.length > 0) {
                if (product.type === EProductType.INSURANCE_ASSISTANCE) {
                  return p.capital_ranges.some((r: any) => {
                    return (
                      (r.description || '') === (product.description || '') &&
                      ((r as any).plan_name || '') === (product.plan_name || '') &&
                      Number((r as any).minimum_price || 0) === Number(product.minimum_price || 0)
                    );
                  });
                } else {
                  return p.capital_ranges.some((r: any) => {
                    return (
                      Number(r.capital_value || 0) === Number(product.capital_value || 0) &&
                      Number(r.premium_value || 0) === Number(product.premium_value || 0)
                    );
                  });
                }
              }
              
              return false;
            });
            
            if (baseProduct) {
              productId = baseProduct.id;
            }
          }
          
          // Fallback: usar product.id se for um UUID válido
          if (!productId) {
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidRegex.test(product.id)) {
              productId = product.id;
            }
          }
          
          // Validar que productId é um UUID válido
          if (!productId) {
            throw new Error(`Produto "${product.name || 'Sem nome'}" não possui um product_id válido (UUID do produto principal).`);
          }
          
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(productId)) {
            throw new Error(`Produto "${product.name || 'Sem nome'}" possui um product_id inválido. O product_id deve ser um UUID válido. Valor: ${productId}`);
          }
          
          return {
            product_id: productId, // UUID do produto principal
            name: product.name || "",
            type: product.type || "",
            capital_value: Number(product.capital_value) || 0,
            premium_value: premiumValue,
            description: product.description || "", // Identifica o capital range para assistências
            // Para assistências, também salvar plan_name e minimum_price para identificar o capital range
            ...(product.type === EProductType.INSURANCE_ASSISTANCE && product.plan_name ? { plan_name: product.plan_name } : {}),
            ...(product.type === EProductType.INSURANCE_ASSISTANCE && product.minimum_price !== null && product.minimum_price !== undefined ? { minimum_price: product.minimum_price } : {}),
            insurance_policy_extra_price_id: product.insurance_policy_extra_price_id || null,
          };
        });
      } catch (error) {
        toast({
          title: t("generic.error"),
          description: error instanceof Error ? error.message : "Erro ao validar produtos. Verifique se todos os produtos têm valores premium válidos.",
          variant: "destructive",
        });
        return;
      }
      
      // Validar que há pelo menos um produto
      if (products.length === 0) {
        toast({
          title: t("generic.error"),
          description: "O plano deve ter pelo menos um produto.",
          variant: "destructive",
        });
        return;
      }
      
      // Calcular plan_price (soma dos premium_value dos produtos)
      const calculatedPlanPrice = products.reduce(
        (total: number, product: any) =>
          total + (Number(product.premium_value) || 0),
        0,
      );

      const allowsZeroPlanTotal = products.some((p: any) =>
        isProductTypeAllowingZeroPlanPremium(p.type),
      );
      const minPlanPrice = allowsZeroPlanTotal ? 0 : 1;
      if (calculatedPlanPrice < minPlanPrice) {
        toast({
          title: t("generic.error"),
          description: allowsZeroPlanTotal
            ? "O preço total do plano não pode ser negativo. Verifique os valores dos produtos."
            : "O preço total do plano deve ser pelo menos R$ 1,00. Verifique os valores dos produtos.",
          variant: "destructive",
        });
        return;
      }
      
      const planData = {
        name: dataToSave.name || "",
        description: dataToSave.description || "",
        products: products,
        commission_distribution: {
          main_partner: mainPartnerCommissions,
          partner_participant: partnerParticipantCommissions,
          partner_affiliate: partnerAffiliateCommissions,
          ...(hunterCommissions.length > 0 && { hunter: hunterCommissions }),
        },
        commission_another: {
          main_partner: mainPartnerAnother,
          partner_participant: partnerParticipantAnother,
          partner_affiliate: partnerAffiliateAnother,
          ...(hunterAnother.length > 0 && { hunter: hunterAnother }),
        },
        plan_price: calculatedPlanPrice,
        plan_features: Array.isArray(dataToSave.plan_features) 
          ? dataToSave.plan_features.flat().filter((feature: any) => typeof feature === 'string' && feature.trim() !== '')
          : [],
        soulhub_anchor_product_id: null,
      };
      
      // Se estiver editando, usar updatePlanMutation, senão createPlanMutation
      if (isEditingPlan && planId) {
        updatePlanMutation.mutate(planData);
      } else {
        createPlanMutation.mutate(planData);
      }
      return;
    }

    // Navegar de volta com os dados salvos (para planos normais, não de projeto)
    navigate(returnPath, {
      state: {
        savedPlanData: dataToSave,
        editingPlanoIndex,
      },
    });
  }, [tempPlano, navigate, returnPath, editingPlanoIndex, validateForm, isEditing, state, projectId, projectType, projectFather, toast, t, queryClient, createPlanMutation]);

  const handleCancelClick = React.useCallback((e?: React.MouseEvent) => {
    // Prevenir comportamento padrão e propagação
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevenir múltiplas navegações simultâneas
    if (isNavigatingRef.current) {
      return;
    }
    isNavigatingRef.current = true;
    
    // Limpar erros e estados antes de navegar
    setValidationErrors({});
    
    // Cancelar qualquer query em andamento que possa estar bloqueando
    queryClient.cancelQueries();
    
    const effectiveProjectId = projectId || params.projectId;
    const effectiveClientId = clientId;
    
    // Navegar imediatamente sem setTimeout para evitar travamentos
    try {
      let targetPath = "/clients";

      // Se for um plano de projeto, voltar para a página de detalhes do projeto
      if (effectiveProjectId && effectiveClientId) {
        targetPath = `/clients/${effectiveClientId}/projects/${effectiveProjectId}`;
      } else if (returnPath && returnPath.trim() !== "") {
        // Se tiver returnPath, usar ele (remover /create-plan se existir)
        targetPath = returnPath.replace('/create-plan', '');
      } else if (effectiveClientId) {
        // Fallback: voltar para a lista de planos do cliente
        targetPath = `/clients/${effectiveClientId}/plans`;
      }

      // Em criação, fazer hard reload para evitar UI travada após navegação
      if (!isEditingPlan) {
        window.location.assign(targetPath);
        return;
      }

      navigate(targetPath, { replace: false });
    } catch (error) {
      // Em caso de erro, resetar o flag e tentar navegação simples
      isNavigatingRef.current = false;
      if (effectiveClientId) {
        navigate(`/clients/${effectiveClientId}/plans`);
      } else {
        navigate("/clients");
      }
    }
  }, [navigate, returnPath, clientId, setValidationErrors, projectId, params.projectId, queryClient, isEditingPlan]);

  // Prevenir navegação duplicada
  const isNavigatingRef = React.useRef(false);

  // Limpar flag de navegação quando o componente desmontar ou quando a localização mudar
  React.useEffect(() => {
    return () => {
      isNavigatingRef.current = false;
    };
  }, []);

  // Resetar flag quando a localização mudar (navegação bem-sucedida)
  React.useEffect(() => {
    isNavigatingRef.current = false;
  }, [location.pathname]);

  // Prevenir que a página bloqueie a UI
  React.useEffect(() => {
    // Garantir que o body/html não tenham pointer-events bloqueado
    document.body.style.pointerEvents = 'auto';
    document.documentElement.style.pointerEvents = 'auto';
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.pointerEvents = "auto";
      rootElement.removeAttribute("aria-hidden");
      rootElement.removeAttribute("inert");
    }

    // Garantir que o overlay de loading global não bloqueie a navegação
    const loadingElement = document.getElementById("loading");
    if (loadingElement) {
      loadingElement.classList.add("hidden");
      loadingElement.style.pointerEvents = "none";
    }
    
    return () => {
      // Limpar ao desmontar
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
      if (rootElement) {
        rootElement.style.pointerEvents = "";
      }
      if (loadingElement) {
        loadingElement.style.pointerEvents = "";
      }
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4" style={{ pointerEvents: 'auto' }}>
      {/* Header */}
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancelClick}
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {(isEditingPlan || isEditing) ? "Editar Plano" : "Adicionar Novo Plano"}
            </h1>
          </div>
        </div>
      </div>

      {/* Conteúdo do formulário */}
      <div className="space-y-6 max-w-7xl mx-auto w-full">
        {/* Tipo de Projeto - Apenas informativo */}
        {projectType && (
        <div>
          <Label>Tipo de Projeto</Label>
            <Input
              value={projectType === "affinity" ? "Afinidade" : "Afiliados"}
              disabled
              className="bg-muted cursor-not-allowed"
            />
        </div>
        )}

        {/* Campos básicos do plano */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nome do Plano</Label>
            <Input
              value={tempPlano.name}
              onChange={(e) =>
                setTempPlano((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Nome do plano"
            />
          </div>

          <div>
            <Label>Descrição</Label>
            <Input
              value={tempPlano.description}
              onChange={(e) =>
                setTempPlano((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Descrição do plano"
            />
          </div>
        </div>

        {/* Características do plano */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              Características do Plano
              <span className="text-red-500 ml-1">*</span>
            </h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (tempPlano.plan_features.length < 10) {
                  setTempPlano((prev) => ({
                    ...prev,
                    plan_features: [...prev.plan_features, ""],
                  }));
                  clearPlanFeaturesError();
                }
              }}
              disabled={tempPlano.plan_features.length >= 10}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar característica
            </Button>
          </div>

          <div className="space-y-3">
            {tempPlano.plan_features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={feature}
                  onChange={(e) => {
                    setTempPlano((prev) => ({
                      ...prev,
                      plan_features: prev.plan_features.map((f, i) =>
                        i === index ? e.target.value : f
                      ),
                    }));
                    clearPlanFeaturesError();
                  }}
                  placeholder={`Característica ${index + 1}`}
                  className={`flex-1 ${
                    validationErrors.planFeatures ? "border-red-500" : ""
                  }`}
                />
                {tempPlano.plan_features.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTempPlano((prev) => ({
                        ...prev,
                        plan_features: prev.plan_features.filter(
                          (_, i) => i !== index
                        ),
                      }));
                      clearPlanFeaturesError();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {tempPlano.plan_features.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma característica adicionada. Clique em "Adicionar
              característica" para começar.
            </p>
          )}

          {tempPlano.plan_features.length >= 10 && (
            <p className="text-sm text-muted-foreground text-center">
              Máximo de 10 características permitidas.
            </p>
          )}

          {validationErrors.planFeatures && (
            <p className="text-sm text-red-500 text-center">
              {validationErrors.planFeatures}
            </p>
          )}
        </div>

        {/* Seleção de produtos */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Produtos do Plano</h4>
          <p className="text-sm text-muted-foreground">
            Selecione os produtos que farão parte deste plano
          </p>

          {(() => {
            const insuranceProducts = selectedProducts.filter(
              (p) => p.type !== EProductType.INSURANCE_ASSISTANCE
            );
            const assistanceProducts = selectedProducts.filter(
              (p) => p.type === EProductType.INSURANCE_ASSISTANCE
            );

            if (selectedProducts.length === 0) {
              return (
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-gray-500 py-8"
                      >
                        Nenhum produto selecionado na aba Produtos.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              );
            }

            return (
              <div className="space-y-8">
                {insuranceProducts.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Selecionar</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Capital</TableHead>
                        <TableHead>Prêmio</TableHead>
                        <TableHead>Descrição</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {insuranceProducts.map((p, i) => {
                        // Usar apenas a posição (índice) como identificador único
                        const uniqueKey = `insurance-${i}`;
                        const positionId = `pos-${i}`;
                        
                        // Função para verificar se um produto corresponde ao produto da lista
                        // Para capital ranges de seguros, usamos product_id + capital_value + premium_value + posição
                        const isProductMatch = (product: any) => {
                          // 1. Comparar por id primeiro (mais confiável) - funciona quando ambos têm o mesmo id
                          if (product.id && p.id && product.id === p.id) {
                            return true;
                          }
                          
                          // 2. Comparar por product_id (UUID do produto principal) - deve ser igual
                          const productIdMatch = (product.product_id || product.id) === (p.product_id || p.id);
                          if (!productIdMatch) {
                            return false;
                          }
                          
                          // 3. Se product_id é igual, comparar por capital_value e premium_value
                          const capitalMatch = Number(product.capital_value) === Number(p.capital_value);
                          const premiumMatch = Number(product.premium_value) === Number(p.premium_value);
                          
                          if (!capitalMatch || !premiumMatch) {
                            return false;
                          }
                          
                          // 4. Para produtos com description ou plan_name (assistências), comparar por esses campos
                          const hasDescriptionOrPlanName = !!(p.description || p.plan_name);
                          
                          if (hasDescriptionOrPlanName) {
                            // Se o produto da lista tem description/plan_name, o produto do plano também deve ter
                            const productHasDescriptionOrPlanName = !!(product.description || product.plan_name);
                            if (!productHasDescriptionOrPlanName) {
                              return false;
                            }
                            
                            // Comparar description se ambos têm
                            if (product.description && p.description) {
                              if (product.description === p.description) {
                                return true;
                              }
                            }
                            
                            // Comparar plan_name se ambos têm
                            if (product.plan_name && p.plan_name) {
                              if (product.plan_name === p.plan_name) {
                                return true;
                              }
                            }
                            
                            // Se um tem description e o outro plan_name, não são iguais
                            return false;
                          }
                          
                          // 5. Para capital ranges de seguros sem description/plan_name:
                          // Usar a posição na lista para identificar qual capital range específico é
                          // Se o produto tem _position armazenado, comparar com a posição atual (i)
                          if (product._position !== undefined) {
                            return product._position === i;
                          }
                          
                          // Se não tem _position, usar apenas product_id + capital_value + premium_value
                          // (pode haver ambiguidade se houver múltiplos capital ranges com os mesmos valores)
                          return true;
                        };
                        
                        return (
                          <TableRow key={uniqueKey}>
                            <TableCell>
                              <Checkbox
                                checked={tempPlano.products.some(isProductMatch)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    // Verificar se o produto já não está selecionado
                                    const isAlreadySelected = tempPlano.products.some(isProductMatch);
                                    if (!isAlreadySelected) {
                                      const productToAdd = {
                                        id: p.id || positionId, // Usar o ID do produto se disponível, senão usar posição
                                        product_id: p.product_id,
                                        name: p.name,
                                        type: p.type,
                                        capital_value: p.capital_value,
                                        premium_value: p.premium_value,
                                        description: p.description,
                                        minimum_price: p.minimum_price,
                                        plan_name: p.plan_name,
                                        insurance_policy_extra_price_id: p.insurance_policy_extra_price_id,
                                        _position: i, // Armazenar a posição para referência
                                      };
                                      setTempPlano((prev) => ({
                                        ...prev,
                                        products: [
                                          ...prev.products,
                                          productToAdd,
                                        ],
                                      }));
                                    }
                                  } else {
                                    setTempPlano((prev) => ({
                                      ...prev,
                                      products: prev.products.filter(
                                        (product) => !isProductMatch(product)
                                      ),
                                    }));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const parentProduct = allProductsSyncMap.get(p.product_id);
                                const synced = isInfinitySyncedProduct(parentProduct);
                                return (
                                  <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="font-medium">
                                        {p.plan_name || p.name || "Sem nome"}
                                      </span>
                                      {synced && (
                                        <Badge
                                          variant="outline"
                                          className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                                        >
                                          <RefreshCw className="h-3 w-3" />
                                          {t("products.sync.badge")}
                                        </Badge>
                                      )}
                                    </div>
                                    {synced &&
                                      (parentProduct?.infinity_sync_source_organizations?.length ?? 0) > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                          {parentProduct!.infinity_sync_source_organizations!.map((label: string) => (
                                            <span
                                              key={label}
                                              className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                              title={label}
                                            >
                                              {label}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>{t(`product.type.${p.type}`)}</TableCell>
                            <TableCell>
                              {formatPrice(p.capital_value)}
                            </TableCell>
                            <TableCell>
                              {formatPrice(p.premium_value)}
                            </TableCell>
                            <TableCell>{p.description}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {assistanceProducts.length > 0 && (
                  <>
                    <div className="border-t" />
                    <h5 className="text-sm font-medium">Assistências</h5>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Selecionar</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Preço mínimo</TableHead>
                          <TableHead>Valor de custo</TableHead>
                          <TableHead>Valor de venda</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>ID do Plano</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {assistanceProducts.map((p, i) => {
                          // Função para verificar se um produto corresponde ao produto da lista
                          // Para capital ranges de assistências, usamos product_id + description + plan_name + minimum_price
                          const isProductMatch = (product: any) => {
                            // 1. Comparar por id primeiro (mais confiável)
                            if (product.id && p.id && product.id === p.id) {
                              return true;
                            }
                            
                            // 2. Comparar por product_id (UUID do produto principal) - deve ser igual
                            const productIdMatch = (product.product_id || product.id) === (p.product_id || p.id);
                            if (!productIdMatch) {
                              return false;
                            }
                            
                            // 3. Para assistências, comparar por description e plan_name (campos mais específicos)
                            // Se ambos têm description, devem ser iguais
                            if (p.description && product.description) {
                              if (product.description !== p.description) {
                                return false;
                              }
                            } else if (p.description || product.description) {
                              // Se apenas um tem description, não são iguais
                              return false;
                            }
                            
                            // Se ambos têm plan_name, devem ser iguais
                            if (p.plan_name && product.plan_name) {
                              if (product.plan_name !== p.plan_name) {
                                return false;
                              }
                            } else if (p.plan_name || product.plan_name) {
                              // Se apenas um tem plan_name, não são iguais
                              return false;
                            }
                            
                            // 4. Comparar por minimum_price (importante para assistências)
                            const minimumPriceMatch = Number(product.minimum_price || 0) === Number(p.minimum_price || 0);
                            if (!minimumPriceMatch) {
                              return false;
                            }
                            
                            // 5. Se chegou até aqui e product_id é igual, description/plan_name são iguais e minimum_price é igual, são o mesmo capital range
                            return true;
                          };
                          
                          return (
                            <TableRow key={`assistance-${i}-${p.id || i}`}>
                              <TableCell>
                                <Checkbox
                                  checked={tempPlano.products.some(isProductMatch)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      // Verificar se o produto já não está selecionado
                                      const isAlreadySelected = tempPlano.products.some(isProductMatch);
                                      if (!isAlreadySelected) {
                                        const productToAdd = {
                                          id: p.id || `assistance-${i}`, // ID do capital range
                                          product_id: p.product_id, // UUID do produto principal (obrigatório)
                                          name: p.name,
                                          type: p.type,
                                          capital_value: p.capital_value,
                                          premium_value: 0,
                                          description: p.description,
                                          minimum_price: p.minimum_price,
                                          plan_name: p.plan_name,
                                          insurance_policy_extra_price_id:
                                            p.insurance_policy_extra_price_id,
                                        };
                                        setTempPlano((prev) => ({
                                          ...prev,
                                          products: [
                                            ...prev.products,
                                            productToAdd,
                                          ],
                                        }));
                                      }
                                    } else {
                                      setTempPlano((prev) => ({
                                        ...prev,
                                        products: prev.products.filter(
                                          (product) => !isProductMatch(product)
                                        ),
                                      }));
                                    }
                                  }}
                                />
                              </TableCell>
                            <TableCell>
                              {(() => {
                                const parentProduct = allProductsSyncMap.get(p.product_id);
                                const synced = isInfinitySyncedProduct(parentProduct);
                                return (
                                  <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="font-medium">
                                        {p.plan_name || p.name || "Sem nome"}
                                      </span>
                                      {synced && (
                                        <Badge
                                          variant="outline"
                                          className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                                        >
                                          <RefreshCw className="h-3 w-3" />
                                          {t("products.sync.badge")}
                                        </Badge>
                                      )}
                                    </div>
                                    {synced &&
                                      (parentProduct?.infinity_sync_source_organizations?.length ?? 0) > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                          {parentProduct!.infinity_sync_source_organizations!.map((label: string) => (
                                            <span
                                              key={label}
                                              className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                              title={label}
                                            >
                                              {label}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {t(`product.type.${p.type}`)}
                            </TableCell>
                            <TableCell>
                              {formatPrice(
                                p.minimum_price ?? p.capital_value
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {formatPrice(
                                  p.type === EProductType.INSURANCE_ASSISTANCE
                                    ? p.premium_value
                                    : 0
                                )}
                              </span>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Encontrar o produto selecionado usando a mesma lógica de comparação robusta
                                const selectedProduct = tempPlano.products.find((prod) => {
                                  // 1. Comparar por id primeiro
                                  if (prod.id && p.id && prod.id === p.id) {
                                    return true;
                                  }
                                  
                                  // 2. Comparar por product_id
                                  const productIdMatch = (prod.product_id || prod.id) === (p.product_id || p.id);
                                  if (!productIdMatch) {
                                    return false;
                                  }
                                  
                                  // 3. Para assistências, comparar por description e plan_name
                                  if (p.description && prod.description) {
                                    if (prod.description !== p.description) {
                                      return false;
                                    }
                                  } else if (p.description || prod.description) {
                                    return false;
                                  }
                                  
                                  if (p.plan_name && prod.plan_name) {
                                    if (prod.plan_name !== p.plan_name) {
                                      return false;
                                    }
                                  } else if (p.plan_name || prod.plan_name) {
                                    return false;
                                  }
                                  
                                  // 4. Comparar por minimum_price
                                  const minimumPriceMatch = Number(prod.minimum_price || 0) === Number(p.minimum_price || 0);
                                  if (!minimumPriceMatch) {
                                    return false;
                                  }
                                  
                                  return true;
                                });
                                const isSelected = Boolean(selectedProduct);
                                if (!isSelected) {
                                  return formatPrice(0);
                                }
                                return (
                                  <div className="max-w-[140px]">
                                    <RealCurrencyInput
                                      value={
                                        selectedProduct?.premium_value ?? 0
                                      }
                                      onChange={(value) => {
                                        setTempPlano((prev) => {
                                          const updatedProducts = prev.products.map(
                                            (prod) => {
                                              // 1. Comparar por id primeiro
                                              if (prod.id && p.id && prod.id === p.id) {
                                                return {
                                                  ...prod,
                                                  premium_value: Number(value) || 0,
                                                };
                                              }
                                              
                                              // 2. Comparar por product_id
                                              const productIdMatch = (prod.product_id || prod.id) === (p.product_id || p.id);
                                              if (!productIdMatch) {
                                                return prod;
                                              }
                                              
                                              // 3. Para assistências, comparar por description e plan_name
                                              if (p.description && prod.description) {
                                                if (prod.description !== p.description) {
                                                  return prod;
                                                }
                                              } else if (p.description || prod.description) {
                                                return prod;
                                              }
                                              
                                              if (p.plan_name && prod.plan_name) {
                                                if (prod.plan_name !== p.plan_name) {
                                                  return prod;
                                                }
                                              } else if (p.plan_name || prod.plan_name) {
                                                return prod;
                                              }
                                              
                                              // 4. Comparar por minimum_price
                                              const minimumPriceMatch = Number(prod.minimum_price || 0) === Number(p.minimum_price || 0);
                                              if (!minimumPriceMatch) {
                                                return prod;
                                              }
                                              
                                              // Se chegou até aqui, são o mesmo capital range
                                              return {
                                                ...prod,
                                                premium_value: Number(value) || 0,
                                              };
                                            }
                                          );
                                          return {
                                            ...prev,
                                            products: updatedProducts,
                                          };
                                        });
                                      }}
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>{p.description}</TableCell>
                            <TableCell>
                              {(() => {
                                // Encontrar o produto selecionado usando a mesma lógica de comparação robusta
                                const selectedProduct = tempPlano.products.find((prod) => {
                                  // 1. Comparar por id primeiro
                                  if (prod.id && p.id && prod.id === p.id) {
                                    return true;
                                  }
                                  
                                  // 2. Comparar por product_id
                                  const productIdMatch = (prod.product_id || prod.id) === (p.product_id || p.id);
                                  if (!productIdMatch) {
                                    return false;
                                  }
                                  
                                  // 3. Para assistências, comparar por description e plan_name
                                  if (p.description && prod.description) {
                                    if (prod.description !== p.description) {
                                      return false;
                                    }
                                  } else if (p.description || prod.description) {
                                    return false;
                                  }
                                  
                                  if (p.plan_name && prod.plan_name) {
                                    if (prod.plan_name !== p.plan_name) {
                                      return false;
                                    }
                                  } else if (p.plan_name || prod.plan_name) {
                                    return false;
                                  }
                                  
                                  // 4. Comparar por minimum_price
                                  const minimumPriceMatch = Number(prod.minimum_price || 0) === Number(p.minimum_price || 0);
                                  if (!minimumPriceMatch) {
                                    return false;
                                  }
                                  
                                  return true;
                                });
                                const isSelected = Boolean(selectedProduct);
                                if (!isSelected) {
                                  return "";
                                }
                                return (
                                  <Input
                                    value={
                                      selectedProduct?.insurance_policy_extra_price_id ??
                                      ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setTempPlano((prev) => ({
                                        ...prev,
                                        products: prev.products.map((prod) => {
                                          // 1. Comparar por id primeiro
                                          if (prod.id && p.id && prod.id === p.id) {
                                            return {
                                              ...prod,
                                              insurance_policy_extra_price_id: value,
                                            };
                                          }
                                          
                                          // 2. Comparar por product_id
                                          const productIdMatch = (prod.product_id || prod.id) === (p.product_id || p.id);
                                          if (!productIdMatch) {
                                            return prod;
                                          }
                                          
                                          // 3. Para assistências, comparar por description e plan_name
                                          if (p.description && prod.description) {
                                            if (prod.description !== p.description) {
                                              return prod;
                                            }
                                          } else if (p.description || prod.description) {
                                            return prod;
                                          }
                                          
                                          if (p.plan_name && prod.plan_name) {
                                            if (prod.plan_name !== p.plan_name) {
                                              return prod;
                                            }
                                          } else if (p.plan_name || prod.plan_name) {
                                            return prod;
                                          }
                                          
                                          // 4. Comparar por minimum_price
                                          const minimumPriceMatch = Number(prod.minimum_price || 0) === Number(p.minimum_price || 0);
                                          if (!minimumPriceMatch) {
                                            return prod;
                                          }
                                          
                                          // Se chegou até aqui, são o mesmo capital range
                                          return {
                                            ...prod,
                                            insurance_policy_extra_price_id: value,
                                          };
                                        }),
                                      }));
                                    }}
                                    className="h-8 text-sm"
                                    placeholder="Informe o código"
                                  />
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* Carregamento comercial */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Carregamento comercial</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newPosition = (tempPlano.installments?.length || 0) + 1;
                setTempPlano((prev) => {
                  const newInstallment = {
                      id: `installment-${Date.now()}`,
                      position: newPosition,
                      mainPartner: {
                      id: projectFather?.id || "",
                      name: projectFather?.name || "",
                        agency: 0,
                        commission: 0,
                      },
                    participatingPartners: (projectParticipantOrganizations || []).map((org) => ({
                      id: org.id,
                      name: org.name,
                      agency: 0,
                      commission: 0,
                    })),
                    hunters: (projectHunters || []).map((hunter) => ({
                      id: hunter.id,
                      name: hunter.name,
                      agency: 0,
                      commission: 0,
                    })),
                  };
                  const updated = {
                    ...prev,
                    installments: [
                      ...(prev.installments || []),
                      newInstallment,
                    ],
                  };
                  
                  // Garantir que otherInstallments esteja inicializado se não estiver
                  if (!updated.otherInstallments && projectFather) {
                    updated.otherInstallments = {
                      mainPartner: {
                        id: projectFather.id,
                        name: projectFather.name,
                        commission: 0,
                        markup: 0,
                      },
                      participatingPartners: (projectParticipantOrganizations || []).map((org) => ({
                        id: org.id,
                        name: org.name,
                        commission: 0,
                        markup: 0,
                      })),
                      hunters: (projectHunters || []).map((hunter) => ({
                        id: hunter.id,
                        name: hunter.name,
                        commission: 0,
                        markup: 0,
                      })),
                    };
                  }
                  
                  return updated;
                });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar parcela
            </Button>
          </div>

          {/* Cards de resumo */}
          {(() => {
            // Calcular totais das parcelas (incluindo hunters)
            const totalAgency = (tempPlano.installments || []).reduce(
              (sum, installment) => {
                const mainAgency = installment.mainPartner?.agency || 0;
                const partnersAgency = (installment.participatingPartners || []).reduce(
                  (pSum, partner) => pSum + (partner.agency || 0),
                  0
                );
                // Incluir agenciamento dos hunters
                const huntersAgency = (installment.hunters || []).reduce(
                  (hSum, hunter) => hSum + (hunter.agency || 0),
                  0
                );
                return sum + mainAgency + partnersAgency + huntersAgency;
              },
              0
            );

            // Calcular totais de mark-up e comissão das demais parcelas (incluindo hunters)
            const otherMainMarkup = tempPlano.otherInstallments?.mainPartner?.markup || 0;
            const otherPartnersMarkup = (tempPlano.otherInstallments?.participatingPartners || []).reduce(
              (sum, partner) => sum + (partner.markup || 0),
              0
            );
            // Incluir mark-up dos hunters das demais parcelas
            const otherHuntersMarkup = (tempPlano.otherInstallments?.hunters || []).reduce(
              (sum, hunter) => sum + (hunter.markup || 0),
              0
            );
            const totalMarkupValue = otherMainMarkup + otherPartnersMarkup + otherHuntersMarkup;

            // Calcular comissão total (incluindo hunters de todas as parcelas)
            const otherMainCommission = tempPlano.otherInstallments?.mainPartner?.commission || 0;
            const otherPartnersCommission = (tempPlano.otherInstallments?.participatingPartners || []).reduce(
              (sum, partner) => sum + (partner.commission || 0),
              0
            );
            // Incluir comissão dos hunters das demais parcelas
            const otherHuntersCommission = (tempPlano.otherInstallments?.hunters || []).reduce(
              (sum, hunter) => sum + (hunter.commission || 0),
              0
            );
            // Incluir comissão dos hunters das parcelas normais
            const installmentsHuntersCommission = (tempPlano.installments || []).reduce(
              (sum, installment) => {
                const huntersCommission = (installment.hunters || []).reduce(
                  (hSum, hunter) => hSum + (hunter.commission || 0),
                  0
                );
                return sum + huntersCommission;
              },
              0
            );
            const totalCommission = otherMainCommission + otherPartnersCommission + otherHuntersCommission + installmentsHuntersCommission;

            return (
              <div className="grid grid-cols-4 gap-4 mb-4">
                <Card className="p-3">
                  <div className="text-sm font-medium text-muted-foreground">
                    Total Agênciamento
                  </div>
                  <div className="text-2xl font-bold">
                    {totalAgency.toFixed(2)}%
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-sm font-medium text-muted-foreground">
                    Mark-up %
                  </div>
                  <div className="text-2xl font-bold">
                    {tempPlano.markup_mode === "percentage"
                      ? (totalMarkupValue / 100).toFixed(2).replace(".", ",")
                      : "0,00"}
                    %
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-sm font-medium text-muted-foreground">
                    Mark-up R$
                  </div>
                  <div className="text-2xl font-bold">
                    R${" "}
                    {tempPlano.markup_mode === "real_currency"
                      ? (totalMarkupValue / 100).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "0,00"}
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-sm font-medium text-muted-foreground">
                    Total Comissão
                  </div>
                  <div className="text-2xl font-bold">
                    {totalCommission.toFixed(2)}%
                  </div>
                </Card>
              </div>
            );
          })()}

          {/* Seleção de Parceiros por Nível (apenas para projetos de afinidade) */}
          {projectType === "affinity" && projectPartners && projectPartners.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Seleção de Parceiros por Nível</h4>
                <p className="text-sm text-muted-foreground">
                  Selecione os parceiros para cada parcela conforme os níveis do projeto
                </p>
              </div>

              {(() => {
                // Agrupar parceiros por nível
                const partnersByLevel = React.useMemo(() => {
                  const grouped: Record<number, typeof projectPartners> = {};
                  projectPartners.forEach((partner) => {
                    if (!grouped[partner.level_hierarchy]) {
                      grouped[partner.level_hierarchy] = [];
                    }
                    grouped[partner.level_hierarchy].push(partner);
                  });
                  return grouped;
                }, [projectPartners]);

                const levels = Object.keys(partnersByLevel)
                  .map(Number)
                  .sort((a, b) => a - b);

                return (
                  <div className="space-y-4">
                    {(tempPlano.installments || []).map(
                      (installment, installmentIndex: number) => (
                        <Card key={`partner-selection-${installmentIndex}`} className="p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="text-sm font-medium">
                              Parcela {installment.position}° - Seleção de Parceiros por Nível
                            </h5>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {levels.map((level) => {
                              const partnersAtLevel = partnersByLevel[level] || [];
                              const partnerOptions = partnersAtLevel.map((partner) => ({
                                value: partner.id,
                                label: `${partner.name} (${(partner.commission_percentage / 100).toFixed(2)}%)`,
                              }));

                              // Obter parceiro selecionado para este nível e parcela
                              const selectedPartnerId = (tempPlano as any).selectedPartners?.[installmentIndex]?.[level];

                              return (
                                <div key={`${installmentIndex}-${level}`} className="space-y-2">
                                  <Label>Nível {level}</Label>
                                  <Combobox
                                    options={partnerOptions}
                                    value={selectedPartnerId}
                                    onChange={(value) => {
                                      setTempPlano((prev) => {
                                        const current = prev as any;
                                        const selectedPartners = current.selectedPartners || {};
                                        if (!selectedPartners[installmentIndex]) {
                                          selectedPartners[installmentIndex] = {};
                                        }
                                        selectedPartners[installmentIndex][level] = value || undefined;
                                        return {
                                          ...prev,
                                          selectedPartners,
                                        };
                                      });
                                    }}
                                    placeholder={`Selecione parceiro do nível ${level}`}
                                    emptyMessage="Nenhum parceiro disponível"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      )
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Parcelas - Para projetos de afinidade e afiliados */}
          {(projectType === "affinity" || projectType === "affiliate") && (
            <div className="space-y-4">
              {(tempPlano.installments && tempPlano.installments.length > 0) ? (
                <div className="space-y-4">
                  {tempPlano.installments.map((installment, installmentIndex) => (
              <Card key={installment.id || `installment-${installmentIndex}`} className="p-4">
                <div className={`flex items-center justify-between ${expandedInstallments.has(installmentIndex) ? "mb-4" : "mb-0"}`}>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-left flex-1"
                    onClick={() => toggleInstallment(installmentIndex)}
                  >
                    {expandedInstallments.has(installmentIndex)
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    }
                    <h5 className="text-sm font-medium">
                      {installment.position}° Parcela
                    </h5>
                  </button>
                  <div className="flex items-center gap-2">
                    {(tempPlano.installments || []).length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        title="Replicar comissionamento desta parcela para todas as outras"
                        onClick={() => {
                          setTempPlano((prev) => ({
                            ...prev,
                            installments: (prev.installments || []).map((inst, i) =>
                              i === installmentIndex
                                ? inst
                                : {
                                    ...inst,
                                    mainPartner: {
                                      ...inst.mainPartner,
                                      agency: installment.mainPartner?.agency ?? 0,
                                      commission: installment.mainPartner?.commission ?? 0,
                                    },
                                    participatingPartners: (inst.participatingPartners || []).map(
                                      (p, pI) => ({
                                        ...p,
                                        agency: installment.participatingPartners?.[pI]?.agency ?? 0,
                                        commission: installment.participatingPartners?.[pI]?.commission ?? 0,
                                      })
                                    ),
                                  }
                            ),
                            otherInstallments: {
                              ...(prev.otherInstallments || {
                                mainPartner: { id: projectFather?.id || "", name: projectFather?.name || "", commission: 0, markup: 0 },
                                participatingPartners: [],
                              }),
                              mainPartner: {
                                ...(prev.otherInstallments?.mainPartner || { id: projectFather?.id || "", name: projectFather?.name || "", commission: 0, markup: 0 }),
                                commission: installment.mainPartner?.commission ?? 0,
                                markup: Math.round((installment.mainPartner?.agency ?? 0) * 100),
                              },
                              participatingPartners: (prev.otherInstallments?.participatingPartners || []).map((p, pI) => ({
                                ...p,
                                commission: installment.participatingPartners?.[pI]?.commission ?? 0,
                                markup: Math.round((installment.participatingPartners?.[pI]?.agency ?? 0) * 100),
                              })),
                            },
                          }));
                        }}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Replicar para todas
                      </Button>
                    )}
                    {(tempPlano.installments || []).length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTempPlano((prev) => ({
                            ...prev,
                            installments: (prev.installments || []).filter(
                              (_, i) => i !== installmentIndex
                            ),
                          }));
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {expandedInstallments.has(installmentIndex) && (
                <div className="space-y-6">
                  {/* Parceiro Principal */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Parceiro Principal</Label>
                      <Input
                        value={installment.mainPartner?.name || ""}
                          readOnly
                          className="mt-2 bg-muted cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Agenciamento (%)</Label>
                        <Input
                          value={installment.mainPartner?.agency !== undefined ? installment.mainPartner.agency : 0}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue = rawValue === "" ? 0 : Number(rawValue);
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            const roundedValue = Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              installments: (prev.installments || []).map((inst, i) =>
                                i === installmentIndex
                                  ? {
                                      ...inst,
                                      mainPartner: {
                                        ...inst.mainPartner,
                                        agency: roundedValue,
                                      },
                                    }
                                  : inst
                              ),
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Comissão (%)</Label>
                        <Input
                          value={installment.mainPartner?.commission !== undefined ? installment.mainPartner.commission : 0}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue = rawValue === "" ? 0 : Number(rawValue);
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            const roundedValue = Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              installments: (prev.installments || []).map((inst, i) =>
                                i === installmentIndex
                                  ? {
                                      ...inst,
                                      mainPartner: {
                                        ...inst.mainPartner,
                                        commission: roundedValue,
                                      },
                                    }
                                  : inst
                              ),
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Parceiros participantes */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">Parceiros participantes</Label>
                    </div>

                    {(installment.participatingPartners || []).length > 1 && (
                      <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-muted/40 rounded-md mb-2">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Aplicar igual p/ todos:</span>
                        <div className="flex items-center gap-1">
                          <Label className="text-xs whitespace-nowrap">Agênc. %</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                            placeholder="0"
                            className="h-7 w-20 text-xs"
                            value={bulkPartnerValues[installmentIndex]?.agency ?? ""}
                            onChange={(e) =>
                              setBulkPartnerValues((prev) => ({
                                ...prev,
                                [installmentIndex]: { ...prev[installmentIndex], agency: e.target.value },
                              }))
                            }
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-xs whitespace-nowrap">Com. %</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                            placeholder="0"
                            className="h-7 w-20 text-xs"
                            value={bulkPartnerValues[installmentIndex]?.commission ?? ""}
                            onChange={(e) =>
                              setBulkPartnerValues((prev) => ({
                                ...prev,
                                [installmentIndex]: { ...prev[installmentIndex], commission: e.target.value },
                              }))
                            }
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            const agency = Math.round(Math.min(99, Math.max(0, parseFloat(bulkPartnerValues[installmentIndex]?.agency || "0") || 0)) * 100) / 100;
                            const commission = Math.round(Math.min(99, Math.max(0, parseFloat(bulkPartnerValues[installmentIndex]?.commission || "0") || 0)) * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              installments: (prev.installments || []).map((inst, i) =>
                                i === installmentIndex
                                  ? {
                                      ...inst,
                                      participatingPartners: (inst.participatingPartners || []).map((p) => ({
                                        ...p,
                                        agency,
                                        commission,
                                      })),
                                    }
                                  : inst
                              ),
                            }));
                          }}
                        >
                          Aplicar
                        </Button>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                    {(installment.participatingPartners || []).map((partner, partnerIndex) => (
                      <div key={`installment-${installmentIndex}-partner-${partnerIndex}`} className="grid grid-cols-3 gap-4 items-end">
                        <Input
                          value={partner.name}
                          readOnly
                          className="bg-muted cursor-not-allowed"
                          placeholder="Nome do parceiro"
                        />
                        <div>
                          <Label className="text-xs">Agenciamento (%)</Label>
                          <Input
                            value={partner.agency || 0}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              let newValue = rawValue === "" ? 0 : Number(rawValue);
                              if (newValue > 99) newValue = 99;
                              if (newValue < 0) newValue = 0;
                              const roundedValue = Math.round(newValue * 100) / 100;
                              setTempPlano((prev) => ({
                                ...prev,
                                installments: (prev.installments || []).map((inst, i) =>
                                  i === installmentIndex
                                    ? {
                                        ...inst,
                                        participatingPartners: (inst.participatingPartners || []).map((p, pI) =>
                                          pI === partnerIndex ? { ...p, agency: roundedValue } : p
                                        ),
                                      }
                                    : inst
                                ),
                              }));
                            }}
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                            className="mt-2"
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-xs">Comissão (%)</Label>
                            <Input
                              value={partner.commission !== undefined ? partner.commission : 0}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                let newValue = rawValue === "" ? 0 : Number(rawValue);
                                if (newValue > 99) newValue = 99;
                                if (newValue < 0) newValue = 0;
                                const roundedValue = Math.round(newValue * 100) / 100;
                                setTempPlano((prev) => ({
                                  ...prev,
                                  installments: (prev.installments || []).map((inst, i) =>
                                    i === installmentIndex
                                      ? {
                                          ...inst,
                                          participatingPartners: (inst.participatingPartners || []).map((p, pI) =>
                                            pI === partnerIndex ? { ...p, commission: roundedValue } : p
                                          ),
                                        }
                                      : inst
                                  ),
                                }));
                              }}
                              type="number"
                              step="0.01"
                              min="0"
                              max="99"
                              className="mt-2"
                            />
                          </div>
                          {(installment.participatingPartners || []).length > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setTempPlano((prev) => ({
                                  ...prev,
                                  installments: (prev.installments || []).map((inst, i) =>
                                    i === installmentIndex
                                      ? {
                                          ...inst,
                                          participatingPartners: (inst.participatingPartners || []).filter(
                                            (_, pI) => pI !== partnerIndex
                                          ),
                                        }
                                      : inst
                                  ),
                                }));
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>

                  {/* Hunters participantes */}
                  {projectHunters && projectHunters.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Hunters participantes</Label>
                      </div>

                      {(installment.hunters || []).map((hunter, hunterIndex) => (
                        <div key={`installment-${installmentIndex}-hunter-${hunterIndex}`} className="grid grid-cols-3 gap-4 items-end">
                          <Input
                            value={hunter.name}
                            readOnly
                            className="bg-muted cursor-not-allowed"
                            placeholder="Nome do hunter"
                          />
                          <div>
                            <Label className="text-xs">Agenciamento (%)</Label>
                            <Input
                              value={hunter.agency || 0}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                let newValue = rawValue === "" ? 0 : Number(rawValue);
                                if (newValue > 99) newValue = 99;
                                if (newValue < 0) newValue = 0;
                                const roundedValue = Math.round(newValue * 100) / 100;
                                setTempPlano((prev) => ({
                                  ...prev,
                                  installments: (prev.installments || []).map((inst, i) =>
                                    i === installmentIndex
                                      ? {
                                          ...inst,
                                          hunters: (inst.hunters || []).map((h, hI) =>
                                            hI === hunterIndex ? { ...h, agency: roundedValue } : h
                                          ),
                                        }
                                      : inst
                                  ),
                                }));
                              }}
                              type="number"
                              step="0.01"
                              min="0"
                              max="99"
                              className="mt-2"
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-xs">Comissão (%)</Label>
                              <Input
                                value={hunter.commission !== undefined ? hunter.commission : 0}
                                onChange={(e) => {
                                  const rawValue = e.target.value;
                                  let newValue = rawValue === "" ? 0 : Number(rawValue);
                                  if (newValue > 99) newValue = 99;
                                  if (newValue < 0) newValue = 0;
                                  const roundedValue = Math.round(newValue * 100) / 100;
                                  setTempPlano((prev) => ({
                                    ...prev,
                                    installments: (prev.installments || []).map((inst, i) =>
                                      i === installmentIndex
                                        ? {
                                            ...inst,
                                            hunters: (inst.hunters || []).map((h, hI) =>
                                              hI === hunterIndex ? { ...h, commission: roundedValue } : h
                                            ),
                                          }
                                        : inst
                                    ),
                                  }));
                                }}
                                type="number"
                                step="0.01"
                                min="0"
                                max="99"
                                className="mt-2"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Comissões por Nível (apenas para projetos de afiliados) */}
                  {projectType === "affiliate" && projectLevelHierarchical && (
                    <div className="space-y-3 pt-6 border-t">
                      <Label className="text-sm font-medium">Comissões por Nível Hierárquico</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: projectLevelHierarchical }, (_, i) => i + 1).map((level) => {
                          const affiliateCommission = (tempPlano as any).affiliateCommissions?.[installmentIndex]?.[level] || {
                            agency: 0,
                            commission: 0,
                          };
                          
                          return (
                            <div key={`level-${level}-installment-${installmentIndex}`} className="space-y-2 p-3 border rounded-md">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline">Nível {level}</Badge>
                              </div>
                              <div>
                                <Label className="text-xs">Agenciamento (%)</Label>
                                <Input
                                  value={affiliateCommission.agency || 0}
                                  onChange={(e) => {
                                    const rawValue = e.target.value;
                                    let newValue = rawValue === "" ? 0 : Number(rawValue);
                                    if (newValue > 99) newValue = 99;
                                    if (newValue < 0) newValue = 0;
                                    const roundedValue = Math.round(newValue * 100) / 100;
                                    setTempPlano((prev) => {
                                      const current = prev as any;
                                      const affiliateCommissions = current.affiliateCommissions || {};
                                      if (!affiliateCommissions[installmentIndex]) {
                                        affiliateCommissions[installmentIndex] = {};
                                      }
                                      affiliateCommissions[installmentIndex][level] = {
                                        ...affiliateCommissions[installmentIndex][level],
                                        agency: roundedValue,
                                        commission: affiliateCommissions[installmentIndex][level]?.commission || 0,
                                      };
                                      return {
                                        ...prev,
                                        affiliateCommissions,
                                      };
                                    });
                                  }}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="99"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Comissão (%)</Label>
                                <Input
                                  value={affiliateCommission.commission || 0}
                                  onChange={(e) => {
                                    const rawValue = e.target.value;
                                    let newValue = rawValue === "" ? 0 : Number(rawValue);
                                    if (newValue > 99) newValue = 99;
                                    if (newValue < 0) newValue = 0;
                                    const roundedValue = Math.round(newValue * 100) / 100;
                                    setTempPlano((prev) => {
                                      const current = prev as any;
                                      const affiliateCommissions = current.affiliateCommissions || {};
                                      if (!affiliateCommissions[installmentIndex]) {
                                        affiliateCommissions[installmentIndex] = {};
                                      }
                                      affiliateCommissions[installmentIndex][level] = {
                                        ...affiliateCommissions[installmentIndex][level],
                                        agency: affiliateCommissions[installmentIndex][level]?.agency || 0,
                                        commission: roundedValue,
                                      };
                                      return {
                                        ...prev,
                                        affiliateCommissions,
                                      };
                                    });
                                  }}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="99"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  Nenhuma parcela configurada. Clique em "Adicionar parcela" para começar.
          </div>
              )}
            </div>
          )}

          {/* Demais Parcelas - Para projetos de afinidade e afiliados */}
          {(projectType === "affinity" || projectType === "affiliate") && (
          <Card className="p-4">
            <CardTitle className="text-sm font-medium mb-4">
              Demais Parcelas
            </CardTitle>
            <div className="space-y-6">
              {/* Parceiro Principal */}
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Parceiro Principal</Label>
                  <Input
                    value={tempPlano.otherInstallments?.mainPartner?.name || ""}
                      readOnly
                      className="mt-2 bg-muted cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">
                        {tempPlano.markup_mode === "real_currency"
                          ? "Mark-up (R$)"
                          : "Mark-up (%)"}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Percent className="w-3 h-3 text-muted-foreground" />
                        <Switch
                          checked={tempPlano.markup_mode === "real_currency"}
                          onCheckedChange={(checked) => {
                            setTempPlano((prev) => ({
                              ...prev,
                              markup_mode: checked
                                ? ("real_currency" as const)
                                : ("percentage" as const),
                            }));
                          }}
                        />
                        <DollarSign className="w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    {tempPlano.markup_mode === "real_currency" ? (
                      <RealCurrencyInput
                        value={tempPlano.otherInstallments?.mainPartner?.markup || 0}
                        onChange={(value) => {
                          setTempPlano((prev) => ({
                            ...prev,
                            otherInstallments: {
                              ...(prev.otherInstallments || {
                                mainPartner: {
                                  id: projectFather?.id || "",
                                  name: projectFather?.name || "",
                                  commission: 0,
                                  markup: 0,
                                },
                                participatingPartners: [],
                              }),
                              mainPartner: {
                                ...(prev.otherInstallments?.mainPartner || {
                                  id: projectFather?.id || "",
                                  name: projectFather?.name || "",
                                  commission: 0,
                                  markup: 0,
                                }),
                                markup: value,
                              },
                            },
                          }));
                        }}
                      />
                    ) : (
                      <Input
                        value={
                          tempPlano.otherInstallments?.mainPartner?.markup !== undefined
                            ? (tempPlano.otherInstallments.mainPartner.markup || 0) / 100
                            : 0
                        }
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          let newValue = rawValue === "" ? 0 : Number(rawValue);
                          if (newValue > 99) newValue = 99;
                          if (newValue < 0) newValue = 0;
                          const roundedValue = Math.round(newValue * 100) / 100;
                          setTempPlano((prev) => ({
                            ...prev,
                            otherInstallments: {
                              ...(prev.otherInstallments || {
                                mainPartner: {
                                  id: projectFather?.id || "",
                                  name: projectFather?.name || "",
                                  commission: 0,
                                  markup: 0,
                                },
                                participatingPartners: [],
                              }),
                              mainPartner: {
                                ...(prev.otherInstallments?.mainPartner || {
                                  id: projectFather?.id || "",
                                  name: projectFather?.name || "",
                                  commission: 0,
                                  markup: 0,
                                }),
                                markup: roundedValue * 100,
                              },
                            },
                          }));
                        }}
                        type="number"
                        step="0.01"
                        min="0"
                        max="99"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Comissão (%)</Label>
                    <Input
                      value={tempPlano.otherInstallments?.mainPartner?.commission !== undefined ? tempPlano.otherInstallments.mainPartner.commission : 0}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        let newValue = rawValue === "" ? 0 : Number(rawValue);
                        if (newValue > 99) newValue = 99;
                        if (newValue < 0) newValue = 0;
                        const roundedValue = Math.round(newValue * 100) / 100;
                        setTempPlano((prev) => ({
                          ...prev,
                          otherInstallments: {
                            ...(prev.otherInstallments || {
                              mainPartner: {
                                id: projectFather?.id || "",
                                name: projectFather?.name || "",
                                commission: 0,
                                markup: 0,
                              },
                              participatingPartners: [],
                            }),
                            mainPartner: {
                              ...(prev.otherInstallments?.mainPartner || {
                                id: projectFather?.id || "",
                                name: projectFather?.name || "",
                                commission: 0,
                                markup: 0,
                              }),
                              commission: roundedValue,
                            },
                          },
                        }));
                      }}
                      type="number"
                      step="0.01"
                      min="0"
                      max="99"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Parceiros participantes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Parceiros participantes</Label>
                  {(tempPlano.otherInstallments?.participatingPartners || []).length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title="Replicar comissionamento do primeiro parceiro para todos os outros"
                      onClick={() => {
                        const first = tempPlano.otherInstallments?.participatingPartners?.[0];
                        if (!first) return;
                        setTempPlano((prev) => ({
                          ...prev,
                          otherInstallments: {
                            ...(prev.otherInstallments || {
                              mainPartner: {
                                id: projectFather?.id || "",
                                name: projectFather?.name || "",
                                commission: 0,
                                markup: 0,
                              },
                              participatingPartners: [],
                            }),
                            participatingPartners: (prev.otherInstallments?.participatingPartners || []).map((p) => ({
                              ...p,
                              markup: first.markup,
                              commission: first.commission,
                            })),
                          },
                        }));
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Replicar para todos
                    </Button>
                  )}
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                {(tempPlano.otherInstallments?.participatingPartners || []).map((partner, partnerIndex) => (
                  <div key={`other-installments-partner-${partnerIndex}`} className="grid grid-cols-3 gap-4 items-end">
                    <Input
                      value={partner.name}
                      readOnly
                      className="bg-muted cursor-not-allowed"
                      placeholder="Nome do parceiro"
                    />
                    <div>
                      <Label className="text-xs">
                        {tempPlano.markup_mode === "real_currency" ? "Mark-up (R$)" : "Mark-up (%)"}
                      </Label>
                      <div className="mt-2">
                      {tempPlano.markup_mode === "real_currency" ? (
                        <RealCurrencyInput
                          value={partner.markup || 0}
                          onChange={(value) => {
                            setTempPlano((prev) => ({
                              ...prev,
                              otherInstallments: {
                                ...(prev.otherInstallments || {
                                  mainPartner: {
                                      id: projectFather?.id || "",
                                      name: projectFather?.name || "",
                                    commission: 0,
                                    markup: 0,
                                  },
                                  participatingPartners: [],
                                }),
                                participatingPartners: (prev.otherInstallments?.participatingPartners || []).map(
                                  (p, pI) => (pI === partnerIndex ? { ...p, markup: value } : p)
                                ),
                              },
                            }));
                          }}
                        />
                      ) : (
                        <Input
                          value={
                            partner.markup !== undefined ? (partner.markup || 0) / 100 : 0
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue = rawValue === "" ? 0 : Number(rawValue);
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            const roundedValue = Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              otherInstallments: {
                                ...(prev.otherInstallments || {
                                  mainPartner: {
                                      id: projectFather?.id || "",
                                      name: projectFather?.name || "",
                                    commission: 0,
                                    markup: 0,
                                  },
                                  participatingPartners: [],
                                }),
                                participatingPartners: (prev.otherInstallments?.participatingPartners || []).map(
                                  (p, pI) => (pI === partnerIndex ? { ...p, markup: roundedValue * 100 } : p)
                                ),
                              },
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                        />
                      )}
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Comissão (%)</Label>
                        <Input
                          value={partner.commission !== undefined ? partner.commission : 0}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            let newValue = rawValue === "" ? 0 : Number(rawValue);
                            if (newValue > 99) newValue = 99;
                            if (newValue < 0) newValue = 0;
                            const roundedValue = Math.round(newValue * 100) / 100;
                            setTempPlano((prev) => ({
                              ...prev,
                              otherInstallments: {
                                ...(prev.otherInstallments || {
                                  mainPartner: {
                                    id: projectFather?.id || "",
                                    name: projectFather?.name || "",
                                    commission: 0,
                                    markup: 0,
                                  },
                                  participatingPartners: [],
                                }),
                                participatingPartners: (prev.otherInstallments?.participatingPartners || []).map(
                                  (p, pI) => (pI === partnerIndex ? { ...p, commission: roundedValue } : p)
                                ),
                              },
                            }));
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          max="99"
                          className="mt-2"
                        />
                      </div>
                      {(tempPlano.otherInstallments?.participatingPartners || []).length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setTempPlano((prev) => ({
                              ...prev,
                              otherInstallments: {
                                ...(prev.otherInstallments || {
                                  mainPartner: {
                                    id: projectFather?.id || "",
                                    name: projectFather?.name || "",
                                    commission: 0,
                                    markup: 0,
                                  },
                                  participatingPartners: [],
                                }),
                                participatingPartners: (prev.otherInstallments?.participatingPartners || []).filter(
                                  (_, pI) => pI !== partnerIndex
                                ),
                              },
                            }));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                </div>
              </div>

              {/* Hunters participantes */}
              {projectHunters && projectHunters.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Hunters participantes</Label>
                  </div>

                  {(tempPlano.otherInstallments?.hunters || []).map((hunter, hunterIndex) => (
                    <div key={`other-installments-hunter-${hunterIndex}`} className="grid grid-cols-3 gap-4 items-end">
                      <Input
                        value={hunter.name}
                        readOnly
                        className="bg-muted cursor-not-allowed"
                        placeholder="Nome do hunter"
                      />
                      <div>
                        <Label className="text-xs">
                          {tempPlano.markup_mode === "real_currency" ? "Mark-up (R$)" : "Mark-up (%)"}
                        </Label>
                        <div className="mt-2">
                        {tempPlano.markup_mode === "real_currency" ? (
                          <RealCurrencyInput
                            value={hunter.markup || 0}
                            onChange={(value) => {
                              setTempPlano((prev) => ({
                                ...prev,
                                otherInstallments: {
                                  ...(prev.otherInstallments || {
                                    mainPartner: {
                                      id: projectFather?.id || "",
                                      name: projectFather?.name || "",
                                      commission: 0,
                                      markup: 0,
                                    },
                                    participatingPartners: [],
                                    hunters: [],
                                  }),
                                  hunters: (prev.otherInstallments?.hunters || []).map(
                                    (h, hI) => (hI === hunterIndex ? { ...h, markup: value } : h)
                                  ),
                                },
                              }));
                            }}
                          />
                        ) : (
                          <Input
                            value={
                              hunter.markup !== undefined ? (hunter.markup || 0) / 100 : 0
                            }
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              let newValue = rawValue === "" ? 0 : Number(rawValue);
                              if (newValue > 99) newValue = 99;
                              if (newValue < 0) newValue = 0;
                              const roundedValue = Math.round(newValue * 100) / 100;
                              setTempPlano((prev) => ({
                                ...prev,
                                otherInstallments: {
                                  ...(prev.otherInstallments || {
                                    mainPartner: {
                                      id: projectFather?.id || "",
                                      name: projectFather?.name || "",
                                      commission: 0,
                                      markup: 0,
                                    },
                                    participatingPartners: [],
                                    hunters: [],
                                  }),
                                  hunters: (prev.otherInstallments?.hunters || []).map(
                                    (h, hI) => (hI === hunterIndex ? { ...h, markup: roundedValue * 100 } : h)
                                  ),
                                },
                              }));
                            }}
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                          />
                        )}
                        </div>
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label className="text-xs">Comissão (%)</Label>
                          <Input
                            value={hunter.commission !== undefined ? hunter.commission : 0}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              let newValue = rawValue === "" ? 0 : Number(rawValue);
                              if (newValue > 99) newValue = 99;
                              if (newValue < 0) newValue = 0;
                              const roundedValue = Math.round(newValue * 100) / 100;
                              setTempPlano((prev) => ({
                                ...prev,
                                otherInstallments: {
                                  ...(prev.otherInstallments || {
                                    mainPartner: {
                                      id: projectFather?.id || "",
                                      name: projectFather?.name || "",
                                      commission: 0,
                                      markup: 0,
                                    },
                                    participatingPartners: [],
                                    hunters: [],
                                  }),
                                  hunters: (prev.otherInstallments?.hunters || []).map((h, hI) =>
                                    hI === hunterIndex ? { ...h, commission: roundedValue } : h
                                  ),
                                },
                              }));
                            }}
                            type="number"
                            step="0.01"
                            min="0"
                            max="99"
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Comissões por Nível (apenas para projetos de afiliados) */}
              {projectType === "affiliate" && projectLevelHierarchical && (
                <div className="space-y-3 pt-6 border-t">
                  <Label className="text-sm font-medium">Comissões por Nível Hierárquico</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: projectLevelHierarchical }, (_, i) => i + 1).map((level) => {
                      const affiliateOtherCommission = (tempPlano as any).affiliateOtherCommissions?.[level] || {
                        markup: 0,
                        commission: 0,
                      };
                      
                      return (
                        <div key={`other-level-${level}`} className="space-y-2 p-3 border rounded-md">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">Nível {level}</Badge>
                          </div>
                          <div>
                            <Label className="text-xs">Mark-up (%)</Label>
                            <Input
                              value={affiliateOtherCommission.markup || 0}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                let newValue = rawValue === "" ? 0 : Number(rawValue);
                                if (newValue > 99) newValue = 99;
                                if (newValue < 0) newValue = 0;
                                const roundedValue = Math.round(newValue * 100) / 100;
                                setTempPlano((prev) => {
                                  const current = prev as any;
                                  const affiliateOtherCommissions = current.affiliateOtherCommissions || {};
                                  affiliateOtherCommissions[level] = {
                                    ...affiliateOtherCommissions[level],
                                    markup: roundedValue,
                                    commission: affiliateOtherCommissions[level]?.commission || 0,
                                  };
                                  return {
                                    ...prev,
                                    affiliateOtherCommissions,
                                  };
                                });
                              }}
                              type="number"
                              step="0.01"
                              min="0"
                              max="99"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Comissão (%)</Label>
                            <Input
                              value={affiliateOtherCommission.commission || 0}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                let newValue = rawValue === "" ? 0 : Number(rawValue);
                                if (newValue > 99) newValue = 99;
                                if (newValue < 0) newValue = 0;
                                const roundedValue = Math.round(newValue * 100) / 100;
                                setTempPlano((prev) => {
                                  const current = prev as any;
                                  const affiliateOtherCommissions = current.affiliateOtherCommissions || {};
                                  affiliateOtherCommissions[level] = {
                                    ...affiliateOtherCommissions[level],
                                    markup: affiliateOtherCommissions[level]?.markup || 0,
                                    commission: roundedValue,
                                  };
                                  return {
                                    ...prev,
                                    affiliateOtherCommissions,
                                  };
                                });
                              }}
                              type="number"
                              step="0.01"
                              min="0"
                              max="99"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex justify-end gap-2 pb-6">
          <Button 
            variant="outline" 
            onClick={handleCancelClick}
          >
            Cancelar
          </Button>
          <Button onClick={handleSaveClick}>
            {(isEditingPlan || isEditing) ? "Salvar Alterações" : "Salvar Plano"}
          </Button>
        </div>
      </div>
    </div>
  );
};

